import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styles from "./templatesManager.module.css"
import api from "../../services/api"
import { toast } from "react-toastify"
import { MdAdd, MdContentCopy, MdOpenInNew, MdOutlineDeleteOutline, MdOutlineEmojiEmotions, MdOutlineFileDownload, MdOutlineFileUpload, MdOutlinePhoneInTalk, MdSend, MdSync, MdViewList, MdViewModule } from "react-icons/md"
import { HiOutlineTemplate } from "react-icons/hi"
import Button from "../../components/Form/Button"
import CharacterCounter from "../../components/Form/CharacterCounter"
import FormGroup from "../../components/Form/FormGroup"
import Input from "../../components/Form/Input"
import InputFile from "../../components/Form/InputFile"
import Select from "../../components/Form/Select"
import Table from "../../components/Table"
import Modal from "../../components/Modal"
import ModalConfirm from "../../components/Modal/ModalConfirm"
import Emojis from "../../components/Chat/Emojis"
import RichTextarea, { insertTextAtCursor } from "../../components/Chat/RichTextarea"
import InfoTooltip from "../../components/InfoTooltip"
import TemplateLibrary from "./TemplateLibrary"
import TemplateCardMenu from "./TemplateCardMenu"
import WhatsAppTemplateCard, { WhatsAppTemplatePreview } from "../../components/WhatsAppTemplateCard"
import { maskPhone } from "../../utils/formatter"
import { openLinkExternal } from "../../utils/utils"
import { useWebSocketChatContext } from "../../contexts/webSocketChatContext"

const headersTemplates = ["Nome", "Categoria", "Idioma", "Status", "Criação", "Componentes", ""]

const limitsTemplateFields = {
    name: 512,
    header: 60,
    body: 1024,
    footer: 60,
    button: 40,
    emojis: 10,
}

const optionsTemplateStatus = [
    { id: "APPROVED", value: "Aprovado" },
    { id: "PENDING", value: "Em análise" },
    { id: "REJECTED", value: "Rejeitado" },
    { id: "PAUSED", value: "Pausado" },
    { id: "DISABLED", value: "Desativado" },
    { id: "DRAFT", value: "Rascunho" },
]

const optionsTemplateCategory = [
    { id: "MARKETING", value: "Marketing" },
    { id: "UTILITY", value: "Utilidade" },
    { id: "AUTHENTICATION", value: "Autenticação" },
]

const optionsTemplatePurpose = [
    { id: "STANDARD", value: "Mensagem padrão" },
    { id: "CALL_PERMISSION_REQUEST", value: "Solicitação de permissão para chamada" },
]

const optionsTemplateLanguage = [
    { id: "pt_BR", value: "Português (Brasil)" },
    { id: "pt_PT", value: "Português (Portugal)" },
    { id: "en_US", value: "Inglês (EUA)" },
    { id: "en_GB", value: "Inglês (Reino Unido)" },
    { id: "es_ES", value: "Espanhol (Espanha)" },
    { id: "es_MX", value: "Espanhol (México)" },
    { id: "fr_FR", value: "Francês" },
    { id: "it_IT", value: "Italiano" },
    { id: "de_DE", value: "Alemão" },
]

const optionsHeaderFormat = [
    { id: "NONE", value: "Sem cabeçalho" },
    { id: "TEXT", value: "Texto" },
    { id: "IMAGE", value: "Imagem" },
    { id: "VIDEO", value: "Vídeo" },
    { id: "DOCUMENT", value: "Documento" },
    { id: "LOCATION", value: "Localização" },
]

const optionsButtonType = [
    { id: "QUICK_REPLY", value: "Resposta rápida" },
    { id: "URL", value: "URL" },
    { id: "PHONE_NUMBER", value: "Telefone" },
    { id: "COPY_CODE", value: "Copiar código" },
]

const optionsUrlButtonMode = [
    { id: "STATIC", value: "Estático" },
    { id: "DYNAMIC", value: "Dinâmico" },
]

const optionsTemplateSubmitMode = [
    { id: "SUBMIT", value: "Enviar para análise" },
    { id: "DRAFT", value: "Salvar como rascunho" },
]

const messageMixedVariableFormats = "Use apenas um tipo de variável no corpo da mensagem: todas com nome, como {{nome_cliente}}, ou todas numeradas, como {{1}}."
const messageInvalidVariables = "Corrija as variáveis indicadas. Use números a partir de 1, como {{1}}, ou nomes em letras minúsculas, como {{nome_cliente}}."
const messageVariableAtEdge = "A Meta não permite variáveis no início ou no fim do corpo da mensagem. Adicione texto antes e depois da variável."
const mediaHeaderFormats = ["IMAGE", "VIDEO", "DOCUMENT"]
const acceptedHeaderMedia = {
    IMAGE: {
        accept: "image/png,image/jpeg,image/jpg",
        label: "imagem",
        types: ["image/png", "image/jpeg", "image/jpg"],
    },
    VIDEO: {
        accept: "video/mp4",
        label: "vídeo MP4",
        types: ["video/mp4"],
    },
    DOCUMENT: {
        accept: "application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation",
        label: "documento",
        types: ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation", "text/plain"],
        extensions: [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".txt"],
    },
}

const defaultTemplateForm = {
    name: "",
    category: "UTILITY",
    language: "pt_BR",
    status: "DRAFT",
    format: "POSITIONAL",
    content: {
        header: null,
        body: {
            text: "",
            examples: [],
            parameters: [],
        },
        footer: null,
        buttons: [],
        call_permission_request: false,
    },
}

const TemplatesManager = ({ linkedAccounts = [], setIsLoading = () => { }, initialInboxId = "", initialTemplateType = "" }) => {
    const { lastJsonMessage } = useWebSocketChatContext()
    const [activeSection, setActiveSection] = useState("account")
    const [templates, setTemplates] = useState([])
    const [templatesByWaba, setTemplatesByWaba] = useState({})
    const [selectedTemplate, setSelectedTemplate] = useState({})
    const [selectedWabaId, setSelectedWabaId] = useState("")
    const [showModalTemplate, setShowModalTemplate] = useState(false)
    const [showModalTemplateForm, setShowModalTemplateForm] = useState(false)
    const [showModalDeleteTemplate, setShowModalDeleteTemplate] = useState(false)
    const [showModalDuplicateTemplates, setShowModalDuplicateTemplates] = useState(false)
    const [showModalImportTemplates, setShowModalImportTemplates] = useState(false)
    const [showModalDeleteSelectedDrafts, setShowModalDeleteSelectedDrafts] = useState(false)
    const [draftSubmissionConfirmation, setDraftSubmissionConfirmation] = useState(null)
    const [templateForm, setTemplateForm] = useState(defaultTemplateForm)
    const [templateFormMode, setTemplateFormMode] = useState("new")
    const [templateIdDelete, setTemplateIdDelete] = useState("")
    const [variableName, setVariableName] = useState("")
    const [selectedTemplateIds, setSelectedTemplateIds] = useState([])
    const [selectedImportTemplateIds, setSelectedImportTemplateIds] = useState([])
    const [selectedImportTemplateId, setSelectedImportTemplateId] = useState("")
    const [duplicateMode, setDuplicateMode] = useState(false)
    const [draftSubmitMode, setDraftSubmitMode] = useState(false)
    const [bulkDraftProgress, setBulkDraftProgress] = useState(null)
    const [duplicateTargetWabaId, setDuplicateTargetWabaId] = useState("")
    const [duplicateTargetInboxId, setDuplicateTargetInboxId] = useState("")
    const [duplicateSubmitMode, setDuplicateSubmitMode] = useState("DRAFT")
    const [importTargetInboxId, setImportTargetInboxId] = useState("")
    const [importNameStrategy, setImportNameStrategy] = useState("RENAME")
    const [importFileName, setImportFileName] = useState("")
    const [importSource, setImportSource] = useState({})
    const [importTemplates, setImportTemplates] = useState([])
    const [templateSubmitMode, setTemplateSubmitMode] = useState("SUBMIT")
    const [templateMedia, setTemplateMedia] = useState([])
    const [templateFormNotice, setTemplateFormNotice] = useState("")
    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const [templatesLoaded, setTemplatesLoaded] = useState(false)
    const [templateViewMode, setTemplateViewMode] = useState("table")
    const [isSynchronizing, setIsSynchronizing] = useState(false)
    const [filtersTemplates, setFiltersTemplates] = useState({
        status: optionsTemplateStatus.map(option => option.id),
        category: optionsTemplateCategory.map(option => option.id),
        language: [],
        textSearch: "",
    })
    const bodyTextareaRef = useRef()
    const emojiPickerRef = useRef()
    const importTemplatesInputRef = useRef()
    const initialTemplateOpenedRef = useRef(false)

    const labelWithInfo = (label, text) => (
        <span className={styles.labelWithInfo}>
            {label}
            <InfoTooltip text={text} preferredPosition="bottom" />
        </span>
    )

    const getWabaIdByAccount = (account = {}) => {
        return account?.metadata?.waba_id || account?.waba_id || account?._id
    }

    const getWabaNameByAccount = (account = {}) => {
        return account?.metadata?.waba_name || account?.metadata?.business_name || account?.waba_name || account?.business_name || account?.account_name || account?.name || "Conta do WhatsApp"
    }

    const getWabaOptionLabel = (wabaAccount = {}) => {
        const phoneNumbers = wabaAccount?.inboxes
            ?.map(inbox => inbox?.metadata?.phone_number)
            ?.filter(Boolean)
            ?.map(maskPhone) || []

        const phones = phoneNumbers.length > 0 ? ` - ${phoneNumbers.join(", ")}` : ""
        return `${wabaAccount?.name || "Conta do WhatsApp"}${phones}`
    }

    const whatsAppAccounts = useMemo(() => {
        const map = new Map()

        linkedAccounts?.forEach(account => {
            const wabaId = getWabaIdByAccount(account)
            if (!wabaId) return

            if (!map.has(wabaId)) {
                map.set(wabaId, {
                    id: wabaId,
                    name: getWabaNameByAccount(account),
                    inbox_id: account?._id,
                    waba_id: wabaId,
                    inboxes: [],
                })
            }

            map.get(wabaId).inboxes.push(account)
        })

        return Array.from(map.values()).map(wabaAccount => ({
            ...wabaAccount,
            value: getWabaOptionLabel(wabaAccount),
        }))
    }, [linkedAccounts])

    const whatsAppAccountsOptions = useMemo(() => {
        return whatsAppAccounts.map(account => ({
            id: account.id,
            value: account.value,
        }))
    }, [whatsAppAccounts])

    const whatsAppInboxOptions = useMemo(() => {
        return linkedAccounts
            ?.filter(account => getWabaIdByAccount(account))
            ?.map(account => ({
                id: account?._id,
                value: `${account?.name || "Caixa de entrada"}${account?.metadata?.phone_number ? ` - ${maskPhone(account.metadata.phone_number)}` : ""}`,
                waba_id: getWabaIdByAccount(account),
            })) || []
    }, [linkedAccounts])

    useEffect(() => {
        if (!selectedWabaId && whatsAppAccountsOptions?.length > 0) {
            const initialWabaId = initialInboxId ? getWabaIdByInboxOption(initialInboxId) : ""
            setSelectedWabaId(initialWabaId || whatsAppAccountsOptions[0]?.id)
        }
    }, [initialInboxId, whatsAppAccountsOptions, selectedWabaId])

    useEffect(() => {
        if (!duplicateTargetWabaId && selectedWabaId) {
            setDuplicateTargetWabaId(selectedWabaId)
        }
    }, [duplicateTargetWabaId, selectedWabaId])

    useEffect(() => {
        if (duplicateTargetInboxId || !selectedWabaId) return

        const selectedInbox = whatsAppInboxOptions.find(option => option?.waba_id === selectedWabaId)
        if (selectedInbox?.id) {
            setDuplicateTargetInboxId(selectedInbox.id)
        }
    }, [duplicateTargetInboxId, selectedWabaId, whatsAppInboxOptions])

    const templateLanguageOptions = useMemo(() => {
        const languages = [...new Set(templates?.map(template => template?.language).filter(Boolean))]
        return languages.sort().map(language => ({ id: language, value: language }))
    }, [templates])

    useEffect(() => {
        setFiltersTemplates(previousValue => ({
            ...previousValue,
            language: templateLanguageOptions.map(option => option.id),
        }))
    }, [templateLanguageOptions])

    const getSelectedWabaAccount = useCallback((wabaId = selectedWabaId) => {
        return whatsAppAccounts.find(account => account?.id === wabaId)
    }, [selectedWabaId, whatsAppAccounts])

    const getRepresentativeInboxIdByWaba = useCallback((wabaId = selectedWabaId) => {
        return getSelectedWabaAccount(wabaId)?.inbox_id
    }, [getSelectedWabaAccount, selectedWabaId])

    const getWabaIdByInboxOption = (inboxId) => {
        return whatsAppInboxOptions.find(option => option?.id === inboxId)?.waba_id || selectedWabaId
    }

    const getAllTemplatesByWaba = useCallback(async (wabaId = selectedWabaId) => {
        try {
            if (!wabaId) {
                toast.error("Selecione uma conta do WhatsApp para listar os templates.")
                return
            }

            setIsLoading(true)
            const response = await api.get(`/integrations/whatsapp-api/templates/${wabaId}`)
            const listTemplates = response?.data?.data || []
            const formattedTemplates = listTemplates.map(template => ({
                ...template,
                status: template?.status || "APPROVED",
                inbox_id: template?.inbox_id || getRepresentativeInboxIdByWaba(wabaId),
                waba_id: wabaId,
            }))

            setTemplates(formattedTemplates)
            setSelectedTemplateIds([])
            setTemplatesByWaba(previousValue => ({
                ...previousValue,
                [wabaId]: formattedTemplates,
            }))
            setTemplatesLoaded(true)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }, [getRepresentativeInboxIdByWaba, selectedWabaId, setIsLoading])

    const synchronizeTemplates = async () => {
        try {
            if (!selectedWabaId || isSynchronizing) return
            setIsSynchronizing(true)
            const response = await api.post(`/integrations/whatsapp-api/templates/${selectedWabaId}/sync`)
            const listTemplates = response?.data?.data || []
            const formattedTemplates = listTemplates.map(template => ({
                ...template,
                status: template?.status || "APPROVED",
                inbox_id: template?.inbox_id || getRepresentativeInboxIdByWaba(selectedWabaId),
                waba_id: selectedWabaId,
            }))
            setTemplates(formattedTemplates)
            setTemplatesByWaba(previousValue => ({ ...previousValue, [selectedWabaId]: formattedTemplates }))
            setTemplatesLoaded(true)
            toast.success(response?.data?.message || "Templates sincronizados com a Meta.")
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível sincronizar os templates.")
        } finally {
            setIsSynchronizing(false)
        }
    }

    useEffect(() => {
        const templateFields = ["message_template_components_update", "message_template_quality_update", "message_template_status_update", "template_category_update", "template_correct_category_detection"]
        if (lastJsonMessage?.event !== "WHATSAPP_META_WEBHOOK" || !templateFields.includes(lastJsonMessage?.content?.field)) return
        const inbox = linkedAccounts.find(item => `${item?._id}` === `${lastJsonMessage?.content?.inbox_id}`)
        if (`${getWabaIdByAccount(inbox) || ""}` !== `${selectedWabaId || ""}`) return
        getAllTemplatesByWaba(selectedWabaId)
    }, [lastJsonMessage, linkedAccounts, selectedWabaId, getAllTemplatesByWaba])

    useEffect(() => {
        if (selectedWabaId && !templatesLoaded) {
            getAllTemplatesByWaba(selectedWabaId)
        }
    }, [getAllTemplatesByWaba, selectedWabaId, templatesLoaded])

    useEffect(() => {
        if (!showEmojiPicker) return

        const handleClickOutsideEmojiPicker = (e) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
                setShowEmojiPicker(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutsideEmojiPicker)

        return () => {
            document.removeEventListener("mousedown", handleClickOutsideEmojiPicker)
        }
    }, [showEmojiPicker])

    const handleChangeTemplateWaba = (wabaId) => {
        setSelectedWabaId(wabaId)
        setDuplicateTargetWabaId(wabaId)
        setDuplicateTargetInboxId(whatsAppInboxOptions.find(option => option?.waba_id === wabaId)?.id || "")
        setDuplicateMode(false)
        setSelectedTemplateIds([])
        setTemplates(templatesByWaba[wabaId] || [])
        setTemplatesLoaded(Boolean(templatesByWaba[wabaId]))
    }

    const handleChangeTemplateFilter = (name, value) => {
        setFiltersTemplates(previousValue => ({ ...previousValue, [name]: value }))
    }

    const getSelectedTemplateInbox = () => {
        return getSelectedWabaAccount()?.inboxes?.[0]
    }

    const getTemplatesManagerUrl = () => {
        const selectedInbox = getSelectedTemplateInbox()
        const wabaId = selectedInbox?.metadata?.waba_id
        const phoneNumber = `${selectedInbox?.metadata?.phone_number || ""}`.replace(/\D/g, "")

        if (wabaId) {
            return `https://business.facebook.com/latest/whatsapp_manager/message_templates?asset_id=${wabaId}${phoneNumber ? `&phone_number=${phoneNumber}` : ""}`
        }

        return "https://business.facebook.com/latest/whatsapp_manager/message_templates"
    }

    const handleOpenTemplatesManager = () => {
        openLinkExternal(getTemplatesManagerUrl())
    }

    const getTemplateStatusLabel = (status) => {
        return optionsTemplateStatus.find(option => option.id === status)?.value || status || "-"
    }

    const getTemplateCategoryLabel = (category) => {
        return optionsTemplateCategory.find(option => option.id === category)?.value || category || "-"
    }

    const getTemplateLanguageLabel = (language) => {
        return optionsTemplateLanguage.find(option => option.id === language)?.value || language || "-"
    }

    const getTemplateComponents = (template) => {
        const components = []
        if (template?.content?.header) components.push("Cabeçalho")
        if (template?.content?.body) components.push("Corpo")
        if (template?.content?.footer) components.push("Rodapé")
        if (template?.content?.buttons?.length > 0) {
            const buttonsCount = template.content.buttons.length
            components.push(buttonsCount === 1 ? "1 botão" : `${buttonsCount} botões`)
        }
        if (template?.content?.call_permission_request) components.push("Permissão para chamada")

        return components.join(", ") || "-"
    }

    const getTemplateCreatedAt = (template = {}) => template?.created_at || template?.created_time

    const formatTemplateCreatedAt = (template = {}, includeTime = false) => {
        const value = getTemplateCreatedAt(template)
        if (!value) return "-"
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return "-"
        return includeTime ? date.toLocaleString("pt-BR") : date.toLocaleDateString("pt-BR")
    }

    const getTemplateActions = (template) => [
        { label: "Visualizar", onClick: handleShowModalTemplate },
        { label: "Editar", onClick: handleOpenEditTemplate },
        { label: "Duplicar", onClick: handleDuplicateTemplate },
        ...(isTemplateDraft(template)
            ? [{ label: "Enviar para análise", onClick: handleSubmitDraftById }]
            : [{ label: "Abrir na Meta", onClick: handleOpenTemplatesManager }]),
        { label: "Excluir", onClick: handleOpenDeleteTemplate },
    ]

    const getTemplateAnalytics = () => {
        const statusMap = templates.reduce((acc, template) => {
            const status = template?.status || "APPROVED"
            acc[status] = (acc[status] || 0) + 1
            return acc
        }, {})
        const categoryMap = templates.reduce((acc, template) => {
            const category = template?.category || "Sem categoria"
            acc[category] = (acc[category] || 0) + 1
            return acc
        }, {})

        return {
            approved: statusMap.APPROVED || 0,
            pending: statusMap.PENDING || 0,
            rejected: statusMap.REJECTED || 0,
            topCategory: Object.entries(categoryMap).sort((a, b) => b[1] - a[1])?.[0]?.[0] || "-",
        }
    }

    const getFilteredTemplateItems = () => {
        const normalizedSearch = filtersTemplates?.textSearch?.toLowerCase()?.trim()?.replaceAll(" ", "_")

        return [...templates]
            .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
            .filter(template => !draftSubmitMode || isTemplateDraft(template))
            .filter(template => !filtersTemplates?.status?.length || filtersTemplates.status.includes(template?.status || "APPROVED"))
            .filter(template => !filtersTemplates?.category?.length || filtersTemplates.category.includes(template?.category))
            .filter(template => !filtersTemplates?.language?.length || filtersTemplates.language.includes(template?.language))
            .filter(template => {
                if (!normalizedSearch) return true

                return (
                    template?.name?.toLowerCase()?.replaceAll(" ", "_").includes(normalizedSearch) ||
                    template?.category?.toLowerCase()?.replaceAll(" ", "_").includes(normalizedSearch) ||
                    template?.language?.toLowerCase()?.replaceAll(" ", "_").includes(normalizedSearch) ||
                    template?.content?.body?.text?.toLowerCase()?.replaceAll(" ", "_").includes(normalizedSearch)
                )
            })
    }

    const getFilteredTemplates = () => {
        return getFilteredTemplateItems()
            .map(template => {
                const canSelectTemplate = !bulkDraftProgress && (duplicateMode || (draftSubmitMode && isTemplateDraft(template)))
                return ({
                id: template?.template_id,
                value: [
                    <span className={canSelectTemplate ? styles.templateNameWithSelect : ""} onClick={canSelectTemplate ? (e) => { e.stopPropagation(); handleToggleTemplateSelection(template?.template_id) } : undefined}>
                        {canSelectTemplate &&
                            <span className={`${styles.selectionBox} ${selectedTemplateIds.includes(template?.template_id) ? styles.checked : ""}`} aria-hidden="true" />
                        }
                        <span className={styles.templateName}>{template?.name || "-"}</span>
                    </span>,
                    getTemplateCategoryLabel(template?.category),
                    template?.language || "-",
                    <span className={`${styles.statusBadge} ${styles[`status_${template?.status || "APPROVED"}`]}`}>
                        {getTemplateStatusLabel(template?.status || "APPROVED")}
                    </span>,
                    <span title={formatTemplateCreatedAt(template, true)}>{formatTemplateCreatedAt(template)}</span>,
                    getTemplateComponents(template),
                ],
                actions: getTemplateActions(template),
                })
            })
    }

    const handleTemplateCardClick = (template) => {
        if (bulkDraftProgress) return
        if (duplicateMode) {
            handleToggleTemplateSelection(template?.template_id)
            return
        }
        if (draftSubmitMode) {
            handleToggleDraftTemplateSelection(template?.template_id)
            return
        }
        handleShowModalTemplate(template?.template_id)
    }

    const renderTemplateCards = () => {
        const filteredTemplates = getFilteredTemplateItems()
        if (!filteredTemplates.length) {
            return <div className={styles.templateCardsEmpty}>{templatesLoaded ? "Nenhum template encontrado" : "Selecione uma conta e sincronize os templates"}</div>
        }

        return <>
            <div className={styles.templateCardsCount}>Registros: {filteredTemplates.length}</div>
            <div className={`${styles.templateCardsGrid} ${bulkDraftProgress ? styles.tableProcessing : ""}`}>
                {filteredTemplates.map(template => {
                    const canSelectTemplate = !bulkDraftProgress && (duplicateMode || (draftSubmitMode && isTemplateDraft(template)))
                    const isSelected = selectedTemplateIds.includes(template?.template_id)
                    return <WhatsAppTemplateCard
                        key={template?.template_id}
                        template={template}
                        title={template?.name || "-"}
                        className={`${isSelected ? styles.selected : ""} ${canSelectTemplate ? styles.selectable : ""}`}
                        onClick={() => handleTemplateCardClick(template)}
                        header={
                            <div className={styles.templateAccountCardHeader}>
                                <div>
                                    {canSelectTemplate && <span className={`${styles.selectionBox} ${isSelected ? styles.checked : ""}`} aria-hidden="true" />}
                                    <span className={`${styles.statusBadge} ${styles[`status_${template?.status || "APPROVED"}`]}`}>
                                        {getTemplateStatusLabel(template?.status || "APPROVED")}
                                    </span>
                                </div>
                                {!duplicateMode && !draftSubmitMode && !bulkDraftProgress && <TemplateCardMenu actions={getTemplateActions(template).map(action => ({
                                    ...action,
                                    onClick: () => action.onClick(template?.template_id),
                                }))} />}
                            </div>
                        }
                        contentClassName={styles.templateAccountCardContent}
                        meta={
                            <div className={styles.templateAccountCardMeta}>
                                <span>{getTemplateCategoryLabel(template?.category)}</span>
                                <span>{getTemplateLanguageLabel(template?.language)}</span>
                            </div>
                        }
                        footer={
                            <div className={styles.templateAccountCardFooter}>
                                <span title={formatTemplateCreatedAt(template, true)}>{getTemplateCreatedAt(template) ? `Criado em ${formatTemplateCreatedAt(template)}` : "Data não disponível"}</span>
                                <span>{getTemplateComponents(template)}</span>
                            </div>
                        }
                    />
                })}
            </div>
        </>
    }

    const downloadJsonFile = (filename, payload) => {
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
    }

    const cleanObject = (data = {}) => {
        return Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined && value !== null && value !== ""))
    }

    const isPlainObject = (value) => {
        return value && typeof value === "object" && !Array.isArray(value)
    }

    const getPortableTemplateContent = (content = {}) => {
        const header = content?.header ? cleanObject({
            format: content.header.format,
            text: content.header.text,
            examples: content.header.examples,
            location: content.header.location,
        }) : null
        const body = cleanObject({
            text: content?.body?.text || "",
            examples: content?.body?.examples || [],
            parameters: content?.body?.parameters || [],
        })
        const footer = content?.footer?.text ? { text: content.footer.text } : null
        const buttons = content?.buttons?.map(button => cleanObject({
            type: button?.type,
            text: button?.text,
            url: button?.url,
            url_mode: button?.url_mode,
            dynamic_variable: button?.dynamic_variable,
            dynamic_example: button?.dynamic_example,
            copy_code_example: button?.copy_code_example,
            flow_id: button?.flow_id,
            phone_number: button?.phone_number,
            examples: button?.examples || [],
        })).filter(button => button?.type && button?.text) || []

        return cleanObject({
            header: header && Object.keys(header).length ? header : null,
            body,
            footer,
            buttons,
            call_permission_request: Boolean(content?.call_permission_request),
        })
    }

    const getPortableTemplate = (template = {}) => cleanObject({
        name: normalizeTemplateName(template?.name || ""),
        category: template?.category || "UTILITY",
        language: template?.language || "pt_BR",
        format: template?.format || "POSITIONAL",
        content: getPortableTemplateContent(template?.content || {}),
    })

    const handleExportTemplates = () => {
        const filteredTemplates = getFilteredTemplateItems()
        if (!filteredTemplates.length) {
            toast.error("Nenhum template encontrado no filtro atual para exportar.")
            return
        }

        const selectedAccount = getSelectedWabaAccount()
        const payload = {
            type: "whatsapp_api_templates",
            version: 1,
            exported_at: new Date().toISOString(),
            source: {
                waba_id: selectedWabaId,
                waba_name: selectedAccount?.name || "",
            },
            filters: filtersTemplates,
            count: filteredTemplates.length,
            templates: filteredTemplates.map(getPortableTemplate),
        }
        const filenameDate = new Date().toISOString().slice(0, 10)
        downloadJsonFile(`whatsapp-api-templates-${selectedWabaId || "export"}-${filenameDate}.json`, payload)
        toast.success(`${filteredTemplates.length} template${filteredTemplates.length === 1 ? "" : "s"} exportado${filteredTemplates.length === 1 ? "" : "s"}.`)
    }

    const getTemplatesFromImportPayload = (payload) => {
        if (Array.isArray(payload)) return payload
        if (Array.isArray(payload?.templates)) return payload.templates
        if (Array.isArray(payload?.data)) return payload.data
        return []
    }

    const getImportPayloadError = (payload, importedTemplates = []) => {
        if (!isPlainObject(payload) && !Array.isArray(payload)) {
            return "O JSON precisa ser um objeto de exportação ou uma lista de templates."
        }

        if (!importedTemplates.length) {
            return "O arquivo não possui templates para importar."
        }

        const invalidIndex = importedTemplates.findIndex(template => !isPlainObject(template))
        if (invalidIndex !== -1) {
            return `O template ${invalidIndex + 1} precisa ser um objeto JSON válido.`
        }

        const missingNameIndex = importedTemplates.findIndex(template => !normalizeTemplateName(template?.name || "").trim())
        if (missingNameIndex !== -1) {
            return `O template ${missingNameIndex + 1} não possui um nome válido.`
        }

        const invalidContentIndex = importedTemplates.findIndex(template => template?.content && !isPlainObject(template.content))
        if (invalidContentIndex !== -1) {
            return `O conteúdo do template ${invalidContentIndex + 1} precisa ser um objeto.`
        }

        const missingBodyIndex = importedTemplates.findIndex(template => !template?.content?.body?.text && !template?.content?.body)
        if (missingBodyIndex !== -1) {
            return `O template ${missingBodyIndex + 1} precisa ter content.body.text.`
        }

        const invalidButtonsIndex = importedTemplates.findIndex(template => template?.content?.buttons && !Array.isArray(template.content.buttons))
        if (invalidButtonsIndex !== -1) {
            return `Os botões do template ${invalidButtonsIndex + 1} precisam estar em uma lista.`
        }

        return ""
    }

    const handleOpenImportTemplates = () => {
        if (!selectedWabaId) {
            toast.error("Selecione uma conta do WhatsApp para importar os templates.")
            return
        }

        importTemplatesInputRef.current?.click()
    }

    const handleCloseImportTemplatesModal = () => {
        setShowModalImportTemplates(false)
        setImportFileName("")
        setImportSource({})
        setImportTemplates([])
        setSelectedImportTemplateIds([])
        setSelectedImportTemplateId("")
        setImportTargetInboxId("")
        setImportNameStrategy("RENAME")
    }

    const handleImportTemplatesFile = async (e) => {
        const file = e.target.files?.[0]
        e.target.value = ""
        if (!file) return

        try {
            const text = await file.text()
            const payload = JSON.parse(text)
            const importedTemplates = getTemplatesFromImportPayload(payload)
            const importPayloadError = getImportPayloadError(payload, importedTemplates)
            if (importPayloadError) {
                toast.error(importPayloadError)
                return
            }

            const templatesToPreview = importedTemplates.map((template, index) => ({
                ...getPortableTemplate(template),
                import_id: `import-${Date.now()}-${index}`,
            }))
            const defaultTargetInboxId = getRepresentativeInboxIdByWaba(selectedWabaId) || ""

            setImportFileName(file.name)
            setImportSource(payload?.source || {
                waba_id: payload?.waba_id,
                waba_name: payload?.waba_name,
            })
            setImportTemplates(templatesToPreview)
            setSelectedImportTemplateIds(templatesToPreview.map(template => template.import_id))
            setSelectedImportTemplateId(templatesToPreview[0]?.import_id || "")
            setImportTargetInboxId(defaultTargetInboxId)
            setShowModalImportTemplates(true)
        } catch (err) {
            toast.error(err instanceof SyntaxError ? "O arquivo selecionado não é um JSON válido." : "Não foi possível ler o arquivo JSON.")
        }
    }

    const handleToggleImportTemplateSelection = (templateId) => {
        setSelectedImportTemplateIds(previousValue => previousValue.includes(templateId)
            ? previousValue.filter(id => id !== templateId)
            : [...previousValue, templateId]
        )
    }

    const handleToggleAllImportTemplates = () => {
        setSelectedImportTemplateIds(previousValue => previousValue.length === importTemplates.length
            ? []
            : importTemplates.map(template => template.import_id)
        )
    }

    const getSelectedImportTemplates = () => {
        return importTemplates.filter(template => selectedImportTemplateIds.includes(template?.import_id))
    }

    const getSelectedImportTemplatePreview = () => {
        return importTemplates.find(template => template?.import_id === selectedImportTemplateId) || importTemplates[0] || {}
    }

    const handleConfirmImportTemplates = async () => {
        try {
            const selectedTemplates = getSelectedImportTemplates()
            if (!selectedTemplates.length) {
                toast.error("Selecione pelo menos um template para importar.")
                return
            }

            const targetWabaId = selectedWabaId
            const targetInboxId = importTargetInboxId || getRepresentativeInboxIdByWaba(targetWabaId)
            if (!targetInboxId) {
                toast.error("Não foi possível identificar uma caixa de entrada para salvar os rascunhos.")
                return
            }

            const targetTemplates = templatesByWaba[targetWabaId] || templates
            const namesInUse = targetTemplates.map(template => template?.name)
            const timestamp = Date.now()
            const drafts = selectedTemplates.map((template, index) => {
                const normalizedTemplate = normalizeTemplateToForm(template, targetWabaId)
                const requestedName = normalizeTemplateName(normalizedTemplate?.name || `template_importado_${index + 1}`)
                const templateName = importNameStrategy === "KEEP"
                    ? requestedName
                    : getUniqueTemplateName(requestedName, namesInUse)
                namesInUse.push(templateName)
                return {
                    ...normalizedTemplate,
                    template_id: `local-import-${timestamp}-${index}`,
                    name: templateName,
                    status: "DRAFT",
                    waba_id: targetWabaId,
                    inbox_id: targetInboxId,
                    is_local_draft: true,
                }
            })

            setIsLoading(true)
            const response = await api.post("/integrations/whatsapp-api/templates/drafts", {
                inbox_id: targetInboxId,
                waba_id: targetWabaId,
                templates: drafts,
            })
            const savedDrafts = response?.data?.data || drafts
            const savedDraftIds = savedDrafts.map(template => template?.template_id)
            const updatedTemplates = [
                ...savedDrafts,
                ...targetTemplates.filter(template => !savedDraftIds.includes(template?.template_id)),
            ]

            setTemplates(updatedTemplates)
            setTemplatesByWaba(previousValue => ({
                ...previousValue,
                [targetWabaId]: updatedTemplates,
            }))
            setTemplatesLoaded(true)
            toast.success(`${savedDrafts.length} template${savedDrafts.length === 1 ? "" : "s"} importado${savedDrafts.length === 1 ? "" : "s"} como rascunho.`)
            handleCloseImportTemplatesModal()
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível importar o arquivo JSON.")
        } finally {
            setIsLoading(false)
        }
    }

    const getSelectedTemplatesToDuplicate = () => {
        return templates.filter(template => selectedTemplateIds.includes(template?.template_id))
    }

    const handleToggleTemplateSelection = (templateId) => {
        setSelectedTemplateIds(previousValue => previousValue.includes(templateId)
            ? previousValue.filter(id => id !== templateId)
            : [...previousValue, templateId]
        )
    }

    const handleToggleDuplicateMode = () => {
        setDraftSubmitMode(false)
        setSelectedTemplateIds([])
        setDuplicateMode(previousValue => {
            const nextValue = !previousValue
            if (!nextValue) {
                setSelectedTemplateIds([])
                setDuplicateTargetWabaId(selectedWabaId)
                setDuplicateTargetInboxId(whatsAppInboxOptions.find(option => option?.waba_id === selectedWabaId)?.id || "")
                setDuplicateSubmitMode("DRAFT")
                setShowModalDuplicateTemplates(false)
            }
            return nextValue
        })
    }

    const handleCloseDraftSubmitMode = () => {
        if (bulkDraftProgress) return
        setDraftSubmitMode(false)
        setSelectedTemplateIds([])
        setShowModalDeleteSelectedDrafts(false)
        setDraftSubmissionConfirmation(null)
    }

    const handleToggleDraftSubmitMode = () => {
        if (bulkDraftProgress) return
        if (draftSubmitMode) {
            handleCloseDraftSubmitMode()
            return
        }

        setDuplicateMode(false)
        setShowModalDuplicateTemplates(false)
        setSelectedTemplateIds([])
        setDraftSubmitMode(true)
    }

    const handleToggleDraftTemplateSelection = (templateId) => {
        if (bulkDraftProgress) return
        const template = templates.find(item => item?.template_id === templateId)
        if (!isTemplateDraft(template)) return
        handleToggleTemplateSelection(templateId)
    }

    const getVisibleDraftIds = () => {
        return getFilteredTemplateItems().filter(isTemplateDraft).map(template => template?.template_id).filter(Boolean)
    }

    const handleToggleAllVisibleDrafts = () => {
        if (bulkDraftProgress) return
        const visibleDraftIds = getVisibleDraftIds()
        setSelectedTemplateIds(previousValue => {
            const allVisibleSelected = visibleDraftIds.length > 0 && visibleDraftIds.every(id => previousValue.includes(id))
            return allVisibleSelected
                ? previousValue.filter(id => !visibleDraftIds.includes(id))
                : [...new Set([...previousValue, ...visibleDraftIds])]
        })
    }

    const handleOpenDeleteSelectedDrafts = () => {
        if (!selectedTemplateIds.length) {
            toast.error("Selecione pelo menos um rascunho para excluir.")
            return
        }
        setShowModalDeleteSelectedDrafts(true)
    }

    const handleCloseDeleteSelectedDrafts = () => {
        setShowModalDeleteSelectedDrafts(false)
    }

    const handleOpenDuplicateTemplatesModal = () => {
        if (!selectedTemplateIds.length) {
            toast.error("Selecione pelo menos um template para duplicar.")
            return
        }

        const defaultInboxId = duplicateTargetInboxId || whatsAppInboxOptions.find(option => option?.waba_id === selectedWabaId)?.id || ""
        const targetWabaId = getWabaIdByInboxOption(defaultInboxId)
        setDuplicateTargetInboxId(defaultInboxId)
        setDuplicateTargetWabaId(targetWabaId)
        setShowModalDuplicateTemplates(true)
    }

    const handleCloseDuplicateTemplatesModal = () => {
        setShowModalDuplicateTemplates(false)
    }

    const handleChangeDuplicateTargetInbox = (inboxId) => {
        setDuplicateTargetInboxId(inboxId)
        setDuplicateTargetWabaId(getWabaIdByInboxOption(inboxId))
    }

    const getUniqueTemplateName = (baseName = "template", existingNames = []) => {
        const normalizedBaseName = normalizeTemplateName(baseName) || "template"
        const usedNames = new Set(existingNames.map(name => normalizeTemplateName(name)).filter(Boolean))

        if (!usedNames.has(normalizedBaseName)) return normalizedBaseName

        let count = 2
        let nextName = normalizeTemplateName(`${normalizedBaseName}_copia`)
        while (usedNames.has(nextName)) {
            nextName = normalizeTemplateName(`${normalizedBaseName}_copia_${count}`)
            count += 1
        }

        return nextName
    }

    const getDuplicatedTemplatePayload = (template, targetWabaId, targetInboxId, templateName, index = 0, timestamp = Date.now()) => ({
        ...normalizeTemplateToForm(template, targetWabaId),
        template_id: `local-${timestamp}-${index}`,
        name: templateName,
        status: "DRAFT",
        waba_id: targetWabaId,
        inbox_id: targetInboxId || getRepresentativeInboxIdByWaba(targetWabaId),
    })

    const handleDuplicateSelectedTemplates = async () => {
        const selectedTemplates = templates.filter(template => selectedTemplateIds.includes(template?.template_id))
        if (!selectedTemplates.length) {
            toast.error("Selecione pelo menos um template para duplicar.")
            return
        }

        const targetInboxId = duplicateTargetInboxId || whatsAppInboxOptions.find(option => option?.waba_id === selectedWabaId)?.id || ""
        const targetWabaId = duplicateTargetWabaId || getWabaIdByInboxOption(targetInboxId) || selectedWabaId
        if (!targetWabaId) {
            toast.error("Selecione para qual caixa de entrada deseja duplicar.")
            return
        }

        const timestamp = Date.now()
        const targetTemplates = templatesByWaba[targetWabaId] || (targetWabaId === selectedWabaId ? templates : [])
        const namesInUse = targetTemplates.map(template => template?.name)
        const duplicates = selectedTemplates.map((template, index) => {
            const templateName = getUniqueTemplateName(template?.name || "template", namesInUse)
            namesInUse.push(templateName)
            return getDuplicatedTemplatePayload(template, targetWabaId, targetInboxId, templateName, index, timestamp)
        })

        if (duplicateSubmitMode === "SUBMIT") {
            try {
                setIsLoading(true)
                const savedTemplates = []
                for (const duplicate of duplicates) {
                    const response = await api.post("/integrations/whatsapp-api/templates", duplicate)
                    savedTemplates.push({
                        ...duplicate,
                        ...(response?.data?.data || {}),
                        inbox_id: duplicate.inbox_id,
                        waba_id: targetWabaId,
                    })
                }

                const updatedTargetTemplates = [...savedTemplates, ...targetTemplates]
                setTemplatesByWaba(previousValue => ({
                    ...previousValue,
                    [targetWabaId]: updatedTargetTemplates,
                }))
                if (targetWabaId === selectedWabaId) {
                    setTemplates(updatedTargetTemplates)
                } else {
                    setSelectedWabaId(targetWabaId)
                    setTemplatesLoaded(false)
                    await getAllTemplatesByWaba(targetWabaId)
                }
                setSelectedTemplateIds([])
                setDuplicateMode(false)
                setShowModalDuplicateTemplates(false)
                setDuplicateSubmitMode("DRAFT")
                toast.success(`${savedTemplates.length} template${savedTemplates.length > 1 ? "s" : ""} enviado${savedTemplates.length > 1 ? "s" : ""} para aprovação.`)
                return
            } catch (err) {
                toast.error(err?.response?.data?.message)
                return
            } finally {
                setIsLoading(false)
            }
        }

        try {
            setIsLoading(true)
            const response = await api.post("/integrations/whatsapp-api/templates/drafts", {
                inbox_id: targetInboxId,
                waba_id: targetWabaId,
                templates: duplicates,
            })
            const savedDrafts = response?.data?.data || duplicates
            const savedDraftIds = savedDrafts.map(template => template?.template_id)
            const updatedTargetTemplates = [
                ...savedDrafts,
                ...targetTemplates.filter(template => !savedDraftIds.includes(template?.template_id)),
            ]
            setTemplatesByWaba(previousValue => ({
                ...previousValue,
                [targetWabaId]: updatedTargetTemplates,
            }))
            if (targetWabaId === selectedWabaId) {
                setTemplates(updatedTargetTemplates)
            } else {
                setSelectedWabaId(targetWabaId)
                setTemplatesLoaded(false)
                await getAllTemplatesByWaba(targetWabaId)
            }
            setSelectedTemplateIds([])
            setDuplicateMode(false)
            setShowModalDuplicateTemplates(false)
            setDuplicateSubmitMode("DRAFT")
            toast.success(response?.data?.message || `${savedDrafts.length} rascunho${savedDrafts.length > 1 ? "s" : ""} salvo${savedDrafts.length > 1 ? "s" : ""}.`)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleShowModalTemplate = (templateId) => {
        const template = templates.find(template => template?.template_id === templateId)
        setSelectedTemplate(template || {})
        setShowModalTemplate(true)
    }

    const isTemplateDraft = (template = {}) => {
        return template?.status === "DRAFT" || template?.is_local_draft === true || `${template?.template_id || ""}`.startsWith("local-")
    }

    const getDraftSubmissionError = (template = {}) => {
        const bodyText = template?.content?.body?.text || ""
        const variables = getVariablesByText(bodyText)
        const variableAnalysis = getVariableAnalysis(bodyText)
        const parameters = template?.content?.body?.parameters || []

        if (!template?.name?.trim()) return "O nome do template é obrigatório."
        if (!bodyText.trim()) return "O corpo do template é obrigatório."
        if (/^\s*{{\w+}}/.test(bodyText) || /{{\w+}}\s*$/.test(bodyText)) return messageVariableAtEdge
        if (variableAnalysis.isMixed) return messageMixedVariableFormats
        if (variableAnalysis.invalid.length) return messageInvalidVariables
        if (/\n{3,}/.test(bodyText.replace(/\r\n/g, "\n"))) return "O corpo do template não pode ter mais de duas quebras de linha consecutivas."
        if (bodyText.trim() && !bodyText.replace(/{{\w+}}/g, "").trim()) return "O corpo do template não pode conter apenas variáveis."
        if (countTemplateEmojis(bodyText) > limitsTemplateFields.emojis) return `O corpo do template pode ter no máximo ${limitsTemplateFields.emojis} emojis.`

        const buttonWithInvalidText = (template?.content?.buttons || []).find(button => `${button?.text || ""}`.length > limitsTemplateFields.button)
        if (buttonWithInvalidText) return `O botão ${buttonWithInvalidText.text} pode ter no máximo ${limitsTemplateFields.button} caracteres.`

        const variableWithoutExample = variables.find(variable => !parameters.find(parameter => parameter?.name === variable)?.example?.trim())
        if (variableWithoutExample) return `Informe uma amostra para a variável {{${variableWithoutExample}}}.`

        const headerFormat = template?.content?.header?.format || "NONE"
        if (mediaHeaderFormats.includes(headerFormat) && !template?.content?.header?.handle) {
            return "Edite o rascunho e selecione uma mídia de exemplo antes de enviar para análise."
        }

        const copyCodeButtonWithoutExample = (template?.content?.buttons || []).find(button => button?.type === "COPY_CODE" && !button?.copy_code_example && !button?.examples?.[0])
        if (copyCodeButtonWithoutExample) return "Informe uma amostra para o botão de copiar código."
        return ""
    }

    const getDraftPayloadForSubmission = (template = {}) => {
        const normalizedTemplate = normalizeTemplateToForm(template, template?.waba_id || selectedWabaId)
        const bodyText = normalizedTemplate?.content?.body?.text || ""
        const detectedFormat = getVariableAnalysis(bodyText).detectedFormat
        const previousParameters = normalizedTemplate?.content?.body?.parameters || []
        const parameters = getVariablesByText(bodyText).map(variable => ({
            name: variable,
            example: previousParameters.find(parameter => parameter?.name === variable)?.example || "",
        }))
        const buttons = (normalizedTemplate?.content?.buttons || []).map(button => {
            if (button?.type === "COPY_CODE") {
                return { ...button, examples: button?.copy_code_example ? [button.copy_code_example] : (button?.examples || []) }
            }
            if (button?.type !== "URL" || button?.url_mode !== "DYNAMIC") return button

            const url = button?.url?.replace(/{{\w+}}/g, "") || ""
            return {
                ...button,
                dynamic_variable: "1",
                url: `${url}{{1}}`,
                examples: button?.dynamic_example ? [button.dynamic_example] : (button?.examples?.length ? button.examples : ["exemplo"]),
            }
        })

        return {
            ...normalizedTemplate,
            format: detectedFormat || normalizedTemplate.format,
            content: {
                ...normalizedTemplate.content,
                body: { ...normalizedTemplate.content.body, parameters },
                buttons,
            },
        }
    }

    const submitDraftToMeta = async (template) => {
        const payload = getDraftPayloadForSubmission(template)
        const targetWabaId = payload?.waba_id || selectedWabaId
        const response = await api.post("/integrations/whatsapp-api/templates", payload)
        const savedTemplate = {
            ...payload,
            ...(response?.data?.data || {}),
            inbox_id: payload?.inbox_id,
            waba_id: targetWabaId,
            is_local_draft: false,
        }

        try {
            await deleteLocalTemplateDraft(payload)
        } catch (err) {
            if (err?.response?.status !== 404) throw err
        }

        return { payload, savedTemplate, targetWabaId, response }
    }

    const handleSubmitDraftForReview = async (template = selectedTemplate) => {
        const errorMessage = getDraftSubmissionError(template)
        if (errorMessage) {
            toast.error(errorMessage)
            return
        }

        try {
            setIsLoading(true)
            const { payload, savedTemplate, targetWabaId, response } = await submitDraftToMeta(template)

            const targetTemplates = templatesByWaba[targetWabaId] || (targetWabaId === selectedWabaId ? templates : [])
            const updatedTemplates = [
                savedTemplate,
                ...targetTemplates.filter(item => item?.template_id !== payload?.template_id && item?.template_id !== savedTemplate?.template_id),
            ]
            setTemplatesByWaba(previousValue => ({ ...previousValue, [targetWabaId]: updatedTemplates }))
            if (targetWabaId === selectedWabaId) setTemplates(updatedTemplates)
            setSelectedTemplateIds(previousValue => previousValue.filter(id => id !== payload?.template_id))
            setTemplatesLoaded(true)
            handleCloseModalTemplate()
            toast.success(response?.data?.message || "Template enviado para análise!")
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível enviar o template para análise.")
        } finally {
            setIsLoading(false)
        }
    }

    const handleOpenDraftSubmissionConfirmation = (drafts = [], scope = "single") => {
        const validDrafts = drafts.filter(isTemplateDraft)
        if (!validDrafts.length) {
            toast.error("Selecione pelo menos um rascunho para enviar.")
            return
        }

        const invalidDraft = validDrafts.find(template => getDraftSubmissionError(template))
        if (invalidDraft) {
            toast.error(`${invalidDraft?.name || "Rascunho"}: ${getDraftSubmissionError(invalidDraft)}`)
            return
        }

        setDraftSubmissionConfirmation({
            scope,
            templateIds: validDrafts.map(template => template?.template_id).filter(Boolean),
        })
    }

    const handleOpenSelectedDraftsSubmissionConfirmation = () => {
        const selectedDrafts = templates.filter(template => selectedTemplateIds.includes(template?.template_id) && isTemplateDraft(template))
        handleOpenDraftSubmissionConfirmation(selectedDrafts, "bulk")
    }

    const handleCloseDraftSubmissionConfirmation = () => {
        setDraftSubmissionConfirmation(null)
    }

    const handleConfirmDraftSubmission = () => {
        const drafts = templates.filter(template => draftSubmissionConfirmation?.templateIds?.includes(template?.template_id) && isTemplateDraft(template))
        const scope = draftSubmissionConfirmation?.scope
        handleCloseDraftSubmissionConfirmation()
        if (scope === "bulk") {
            handleSubmitSelectedDraftsForReview(drafts)
        } else if (drafts[0]) {
            handleSubmitDraftForReview(drafts[0])
        }
    }

    const handleSubmitDraftById = (templateId) => {
        const template = templates.find(item => item?.template_id === templateId)
        if (!isTemplateDraft(template)) {
            toast.error("Rascunho não encontrado.")
            return
        }
        handleOpenDraftSubmissionConfirmation([template], "single")
    }

    const handleSubmitSelectedDraftsForReview = async (drafts = null) => {
        const selectedDrafts = Array.isArray(drafts)
            ? drafts.filter(isTemplateDraft)
            : templates.filter(template => selectedTemplateIds.includes(template?.template_id) && isTemplateDraft(template))
        if (!selectedDrafts.length) {
            toast.error("Selecione pelo menos um rascunho para enviar.")
            return
        }

        const invalidDraft = selectedDrafts.find(template => getDraftSubmissionError(template))
        if (invalidDraft) {
            toast.error(`${invalidDraft?.name || "Rascunho"}: ${getDraftSubmissionError(invalidDraft)}`)
            return
        }

        try {
            setBulkDraftProgress({ action: "Enviando para análise", current: 0, total: selectedDrafts.length, currentName: selectedDrafts[0]?.name || "" })
            const submitted = []
            const failed = []

            for (let index = 0; index < selectedDrafts.length; index += 1) {
                const template = selectedDrafts[index]
                setBulkDraftProgress({ action: "Enviando para análise", current: index, total: selectedDrafts.length, currentName: template?.name || "" })
                try {
                    submitted.push(await submitDraftToMeta(template))
                } catch (err) {
                    failed.push({ template, message: err?.response?.data?.message || "Não foi possível enviar para análise." })
                }
                setBulkDraftProgress({ action: "Enviando para análise", current: index + 1, total: selectedDrafts.length, currentName: template?.name || "" })
            }

            if (submitted.length) {
                const submittedDraftIds = new Set(submitted.map(item => item?.payload?.template_id))
                const submittedTemplateIds = new Set(submitted.map(item => item?.savedTemplate?.template_id))
                const savedTemplates = submitted.map(item => item.savedTemplate)
                const updatedTemplates = [
                    ...savedTemplates,
                    ...templates.filter(template => !submittedDraftIds.has(template?.template_id) && !submittedTemplateIds.has(template?.template_id)),
                ]
                setTemplates(updatedTemplates)
                setTemplatesByWaba(previousValue => ({ ...previousValue, [selectedWabaId]: updatedTemplates }))
                setTemplatesLoaded(true)
                toast.success(`${submitted.length} template${submitted.length === 1 ? "" : "s"} enviado${submitted.length === 1 ? "" : "s"} para análise!`)
            }

            setSelectedTemplateIds(failed.map(item => item?.template?.template_id).filter(Boolean))
            setDraftSubmitMode(failed.length > 0)
            if (failed.length) {
                toast.error(`${failed.length} template${failed.length === 1 ? "" : "s"} não ${failed.length === 1 ? "foi enviado" : "foram enviados"}. ${failed[0].message}`)
            }
        } finally {
            setBulkDraftProgress(null)
        }
    }

    const handleDeleteSelectedDrafts = async () => {
        const selectedDrafts = templates.filter(template => selectedTemplateIds.includes(template?.template_id) && isTemplateDraft(template))
        if (!selectedDrafts.length) {
            toast.error("Selecione pelo menos um rascunho para excluir.")
            handleCloseDeleteSelectedDrafts()
            return
        }

        try {
            setShowModalDeleteSelectedDrafts(false)
            setBulkDraftProgress({ action: "Excluindo rascunhos", current: 0, total: selectedDrafts.length, currentName: selectedDrafts[0]?.name || "" })
            const deletedIds = []
            const failed = []

            for (let index = 0; index < selectedDrafts.length; index += 1) {
                const template = selectedDrafts[index]
                setBulkDraftProgress({ action: "Excluindo rascunhos", current: index, total: selectedDrafts.length, currentName: template?.name || "" })
                try {
                    await deleteLocalTemplateDraft(template)
                    deletedIds.push(template.template_id)
                } catch (err) {
                    if (err?.response?.status === 404) {
                        deletedIds.push(template.template_id)
                    } else {
                        failed.push({ template, message: err?.response?.data?.message || "Não foi possível excluir o rascunho." })
                    }
                }
                setBulkDraftProgress({ action: "Excluindo rascunhos", current: index + 1, total: selectedDrafts.length, currentName: template?.name || "" })
            }

            if (deletedIds.length) {
                const deletedIdSet = new Set(deletedIds)
                const updatedTemplates = templates.filter(template => !deletedIdSet.has(template?.template_id))
                setTemplates(updatedTemplates)
                setTemplatesByWaba(previousValue => ({ ...previousValue, [selectedWabaId]: updatedTemplates }))
                setTemplatesLoaded(true)
                toast.success(`${deletedIds.length} rascunho${deletedIds.length === 1 ? "" : "s"} excluído${deletedIds.length === 1 ? "" : "s"}!`)
            }

            setSelectedTemplateIds(failed.map(item => item?.template?.template_id).filter(Boolean))
            setDraftSubmitMode(failed.length > 0)
            setShowModalDeleteSelectedDrafts(false)
            if (failed.length) {
                toast.error(`${failed.length} rascunho${failed.length === 1 ? "" : "s"} não ${failed.length === 1 ? "foi excluído" : "foram excluídos"}. ${failed[0].message}`)
            }
        } finally {
            setBulkDraftProgress(null)
        }
    }

    const handleCloseModalTemplate = () => {
        setSelectedTemplate({})
        setShowModalTemplate(false)
    }

    const handleOpenNewTemplate = () => {
        setTemplateForm({
            ...defaultTemplateForm,
            template_id: `local-${Date.now()}`,
            inbox_id: getRepresentativeInboxIdByWaba(selectedWabaId),
            waba_id: selectedWabaId,
        })
        setTemplateFormMode("new")
        setTemplateSubmitMode("SUBMIT")
        setTemplateFormNotice("")
        setShowModalTemplateForm(true)
    }

    const handleOpenNewCallPermissionTemplate = useCallback(() => {
        setTemplateForm({
            ...defaultTemplateForm,
            template_id: `local-${Date.now()}`,
            inbox_id: initialInboxId || getRepresentativeInboxIdByWaba(selectedWabaId),
            waba_id: selectedWabaId,
            name: "solicitacao_permissao_chamada",
            category: "UTILITY",
            language: "pt_BR",
            content: {
                ...defaultTemplateForm.content,
                body: {
                    ...defaultTemplateForm.content.body,
                    text: "Gostaríamos de ligar para você pelo WhatsApp para continuar este atendimento. Você autoriza esta chamada?",
                },
                call_permission_request: true,
            },
        })
        setTemplateFormMode("new")
        setTemplateSubmitMode("SUBMIT")
        setTemplateFormNotice("")
        setShowModalTemplateForm(true)
        initialTemplateOpenedRef.current = true
    }, [getRepresentativeInboxIdByWaba, initialInboxId, selectedWabaId])

    useEffect(() => {
        if (
            initialTemplateType === "call_permission_request"
            && selectedWabaId
            && !initialTemplateOpenedRef.current
        ) {
            handleOpenNewCallPermissionTemplate()
        }
    }, [handleOpenNewCallPermissionTemplate, initialTemplateType, selectedWabaId])

    const handleOpenEditTemplate = (templateId) => {
        const template = templates.find(template => template?.template_id === templateId)
        if (!template) return

        const isLocalDraft = `${templateId}`.startsWith("local-")
        const canEditSameTemplate = template?.status === "REJECTED"
        if (!isLocalDraft && !canEditSameTemplate) {
            setTemplateForm({
                ...normalizeTemplateToForm(template, selectedWabaId),
                template_id: `local-${Date.now()}`,
                name: getUniqueTemplateName(template?.name || "template", templates.map(template => template?.name)),
                status: "DRAFT",
            })
            setTemplateFormMode("duplicate")
            setTemplateSubmitMode("SUBMIT")
            setTemplateFormNotice("Por uma limitação da API da Meta, templates já aprovados não podem ser alterados diretamente. Vamos criar uma nova versão com as suas alterações e enviar para aprovação. O template atual continua disponível enquanto a nova versão é analisada.")
            toast.info("Vamos criar uma nova versão deste template e enviar para aprovação.")
        } else {
            setTemplateForm(normalizeTemplateToForm(template, selectedWabaId))
            setTemplateFormMode(isLocalDraft ? "duplicate" : "edit")
            setTemplateSubmitMode(isLocalDraft ? "DRAFT" : "SUBMIT")
            setTemplateFormNotice("")
        }
        setShowModalTemplate(false)
        setShowModalTemplateForm(true)
    }

    const handleDuplicateTemplate = (templateId) => {
        const template = templates.find(template => template?.template_id === templateId)
        if (!template) return

        setTemplateForm({
            ...normalizeTemplateToForm(template, selectedWabaId),
            template_id: `local-${Date.now()}`,
            name: getUniqueTemplateName(template?.name || "template", templates.map(template => template?.name)),
            status: "DRAFT",
        })
        setTemplateFormMode("duplicate")
        setTemplateSubmitMode("SUBMIT")
        setTemplateFormNotice("")
        setShowModalTemplate(false)
        setShowModalTemplateForm(true)
    }

    const handleOpenDeleteTemplate = (templateId) => {
        setTemplateIdDelete(templateId)
        setShowModalDeleteTemplate(true)
    }

    const handleCloseDeleteTemplate = () => {
        setTemplateIdDelete("")
        setShowModalDeleteTemplate(false)
    }

    const handleCloseModalTemplateForm = () => {
        setTemplateForm(defaultTemplateForm)
        setTemplateMedia([])
        setTemplateFormMode("new")
        setTemplateSubmitMode("SUBMIT")
        setTemplateFormNotice("")
        setShowModalTemplateForm(false)
    }

    const normalizeTemplateName = (name = "") => {
        return name
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .trimStart()
            .replace(/\s+/g, "_")
            .replace(/[^a-z0-9_]/g, "")
            .slice(0, limitsTemplateFields.name)
    }

    const limitText = (text = "", limit) => `${text}`.slice(0, limit)

    const getCounter = (text = "", limit) => <CharacterCounter value={text} limit={limit} />

    const normalizeTemplateToForm = (template, wabaId = selectedWabaId) => ({
        template_id: template?.template_id,
        inbox_id: template?.inbox_id || getRepresentativeInboxIdByWaba(wabaId),
        waba_id: template?.waba_id || wabaId,
        name: template?.name || "",
        category: template?.category || "UTILITY",
        language: template?.language || "pt_BR",
        status: template?.status || "DRAFT",
        format: template?.format || "POSITIONAL",
        content: {
            header: template?.content?.header || null,
            body: {
                text: template?.content?.body?.text || "",
                examples: template?.content?.body?.examples || [],
                parameters: template?.content?.body?.parameters || [],
            },
            footer: template?.content?.footer || null,
            buttons: template?.content?.buttons?.map(button => ({
                ...button,
                url_mode: button?.url?.includes("{{") ? "DYNAMIC" : (button?.url_mode || "STATIC"),
                dynamic_variable: button?.url?.includes("{{") || button?.url_mode === "DYNAMIC" ? "1" : "",
            })) || [],
            call_permission_request: Boolean(template?.content?.call_permission_request),
        },
    })

    const handleChangeTemplateForm = (field, value) => {
        setTemplateForm(previousValue => {
            const nextValue = field === "name" ? normalizeTemplateName(value) : value
            return {
                ...previousValue,
                [field]: nextValue,
                ...(field === "waba_id" ? { inbox_id: getRepresentativeInboxIdByWaba(nextValue) } : {}),
            }
        })
    }

    const handleChangeTemplateContent = (component, field, value) => {
        const limits = {
            header: limitsTemplateFields.header,
            body: limitsTemplateFields.body,
            footer: limitsTemplateFields.footer,
        }
        const nextValue = field === "text" && limits[component] ? limitText(value, limits[component]) : value

        setTemplateForm(previousValue => ({
            ...previousValue,
            content: {
                ...previousValue.content,
                [component]: component === "footer" && !nextValue ? null : {
                    ...previousValue.content?.[component],
                    [field]: nextValue,
                },
            },
        }))
    }

    const handleChangeHeaderFormat = (format) => {
        setTemplateMedia([])
        setTemplateForm(previousValue => ({
            ...previousValue,
            content: {
                ...previousValue.content,
                header: format === "NONE" ? null : {
                    format,
                    text: format === "TEXT" ? previousValue.content?.header?.text || "" : undefined,
                    handle: mediaHeaderFormats.includes(format) ? previousValue.content?.header?.handle || "" : undefined,
                },
            },
        }))
    }

    const getHeaderMediaConfig = (format = getHeaderFormatSelected()) => {
        return acceptedHeaderMedia[format] || acceptedHeaderMedia.DOCUMENT
    }

    const isValidHeaderMediaFile = (file, format = getHeaderFormatSelected()) => {
        if (!file) return false

        const config = getHeaderMediaConfig(format)
        const filename = `${file?.name || ""}`.toLowerCase()
        const extensionIsValid = config.extensions?.some(extension => filename.endsWith(extension))

        return config.types.includes(file?.type) || Boolean(extensionIsValid)
    }

    const handleChangeTemplateMedia = (files = []) => {
        const file = files?.[0]
        if (!file) {
            setTemplateMedia([])
            return
        }

        const config = getHeaderMediaConfig()
        if (!isValidHeaderMediaFile(file)) {
            toast.error(`Selecione um arquivo de ${config.label} para usar no cabeçalho.`)
            setTemplateMedia([])
            return
        }

        setTemplateMedia([file])
        handleChangeTemplateContent("header", "handle", "")
    }

    const uploadTemplateMediaFile = async (file) => {
        const formData = new FormData()
        formData.append("media", file)
        formData.append("waba_id", templateForm?.waba_id || selectedWabaId)

        const response = await api.post("/integrations/whatsapp-api/templates/media", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        })

        return response?.data?.data?.handle || ""
    }

    const handleChangeButton = (index, field, value) => {
        setTemplateForm(previousValue => ({
            ...previousValue,
            content: {
                ...previousValue.content,
                buttons: previousValue.content?.buttons?.map((button, buttonIndex) => {
                    if (buttonIndex !== index) return button
                    const nextValue = field === "text"
                        ? limitText(value, limitsTemplateFields.button)
                        : (field === "dynamic_variable" ? normalizeVariableName(value) : value)
                    const nextButton = { ...button, [field]: nextValue }
                    if (field === "type" && value === "URL" && !nextButton.url_mode) {
                        nextButton.url_mode = "STATIC"
                    }
                    if (field === "url_mode" && value === "DYNAMIC") {
                        nextButton.dynamic_variable = "1"
                    }
                    if (field === "url_mode" && value === "STATIC") {
                        nextButton.dynamic_variable = ""
                        nextButton.dynamic_example = ""
                        nextButton.examples = []
                    }
                    return nextButton
                }) || [],
            },
        }))
    }

    const handleAddButton = () => {
        setTemplateForm(previousValue => ({
            ...previousValue,
            content: {
                ...previousValue.content,
                buttons: [
                    ...(previousValue.content?.buttons || []),
                    {
                        type: "QUICK_REPLY",
                        text: "",
                        url_mode: "STATIC",
                    },
                ].slice(0, 3),
            },
        }))
    }

    const handleRemoveButton = (index) => {
        setTemplateForm(previousValue => ({
            ...previousValue,
            content: {
                ...previousValue.content,
                buttons: previousValue.content?.buttons?.filter((_, buttonIndex) => buttonIndex !== index) || [],
            },
        }))
    }

    const getTemplatePayloadByForm = (contentOverride = {}) => {
        const formContent = {
            ...templateForm.content,
            ...contentOverride,
            header: contentOverride.header ? {
                ...templateForm.content?.header,
                ...contentOverride.header,
            } : templateForm.content?.header,
            body: contentOverride.body ? {
                ...templateForm.content?.body,
                ...contentOverride.body,
            } : templateForm.content?.body,
        }
        const bodyText = formContent?.body?.text || ""
        const detectedFormat = getVariableAnalysis(bodyText).detectedFormat
        const parameters = getVariablesByText(bodyText).map(variable => ({
            name: variable,
            example: getVariableExample(variable),
        }))

        const formattedButtons = formContent?.buttons?.filter(button => button?.text)?.map(button => {
            if (button?.type === "COPY_CODE") {
                return {
                    ...button,
                    examples: button?.copy_code_example ? [button.copy_code_example] : (button?.examples || []),
                }
            }

            if (button?.type !== "URL" || button?.url_mode !== "DYNAMIC") return button

            const url = button?.url?.replace(/{{\w+}}/g, "") || ""
            return {
                ...button,
                dynamic_variable: "1",
                url: `${url}{{1}}`,
                examples: button?.dynamic_example ? [button.dynamic_example] : (button?.examples?.length ? button.examples : ["exemplo"]),
            }
        }) || []

        return {
            ...templateForm,
            format: detectedFormat || templateForm.format,
            name: normalizeTemplateName(templateForm?.name),
            content: {
                ...formContent,
                body: {
                    ...formContent?.body,
                    text: bodyText,
                    parameters,
                },
                footer: formContent?.footer?.text ? formContent.footer : null,
                buttons: formattedButtons,
            },
        }
    }

    const getVariablesByText = (text = "") => {
        const matches = [...text.matchAll(/{{(\w+)}}/g)]
        return [...new Set(matches.map(match => match[1]))]
    }

    const getVariableOccurrences = (text = "") => {
        return [...`${text}`.matchAll(/{{(\w+)}}/g)].map(match => match[1])
    }

    const handleChangeTemplatePurpose = (purpose) => {
        const isCallPermissionRequest = purpose === "CALL_PERMISSION_REQUEST"
        setTemplateForm(previousValue => ({
            ...previousValue,
            category: isCallPermissionRequest ? "UTILITY" : previousValue.category,
            format: isCallPermissionRequest ? "POSITIONAL" : previousValue.format,
            content: {
                ...previousValue.content,
                header: isCallPermissionRequest ? null : previousValue.content?.header,
                footer: isCallPermissionRequest ? null : previousValue.content?.footer,
                buttons: isCallPermissionRequest ? [] : previousValue.content?.buttons || [],
                call_permission_request: isCallPermissionRequest,
                body: {
                    ...previousValue.content?.body,
                    text: isCallPermissionRequest && !previousValue.content?.body?.text
                        ? "Gostaríamos de ligar para você pelo WhatsApp para continuar este atendimento. Você autoriza esta chamada?"
                        : previousValue.content?.body?.text || "",
                },
            },
        }))
    }

    const countTemplateEmojis = (text = "") => {
        return [...`${text}`.matchAll(/\p{Extended_Pictographic}/gu)].length
    }

    const getTemplateBodyRuleErrors = () => {
        const bodyText = templateForm?.content?.body?.text || ""
        const errors = []
        if (templateForm?.content?.call_permission_request && getVariablesByText(bodyText).length > 0) {
            errors.push("Use texto estático na solicitação de chamada. Esse fluxo não aceita variáveis.")
        }
        if (/\n{3,}/.test(bodyText.replace(/\r\n/g, "\n"))) {
            errors.push("O corpo do template não pode ter mais de duas quebras de linha consecutivas.")
        }
        if (bodyText.trim() && !bodyText.replace(/{{\w+}}/g, "").trim()) {
            errors.push("O corpo do template não pode conter apenas variáveis.")
        }
        if (countTemplateEmojis(bodyText) > limitsTemplateFields.emojis) {
            errors.push(`O corpo do template pode ter no máximo ${limitsTemplateFields.emojis} emojis.`)
        }
        return errors
    }

    const getButtonRuleErrors = () => {
        return (templateForm?.content?.buttons || []).reduce((errors, button, index) => {
            if (`${button?.text || ""}`.length > limitsTemplateFields.button) {
                errors.push(`O botão ${index + 1} pode ter no máximo ${limitsTemplateFields.button} caracteres.`)
            }
            return errors
        }, [])
    }

    const getDuplicatedVariables = () => {
        const occurrences = getVariableOccurrences(templateForm?.content?.body?.text || "")
        return [...new Set(occurrences.filter((variable, index) => occurrences.indexOf(variable) !== index))]
    }

    const getVariableExample = (variable) => {
        const parameter = templateForm?.content?.body?.parameters?.find(parameter => parameter?.name === variable)
        return parameter?.example || ""
    }

    const handleChangeVariableExample = (variable, example) => {
        setTemplateForm(previousValue => {
            const previousParameters = previousValue?.content?.body?.parameters || []
            const exists = previousParameters.some(parameter => parameter?.name === variable)
            const parameters = exists
                ? previousParameters.map(parameter => parameter?.name === variable ? { ...parameter, example } : parameter)
                : [...previousParameters, { name: variable, example }]

            return {
                ...previousValue,
                content: {
                    ...previousValue.content,
                    body: {
                        ...previousValue.content?.body,
                        parameters,
                    },
                },
            }
        })
    }

    const hasVariableAtEdge = () => {
        const bodyText = templateForm?.content?.body?.text || ""
        return /^\s*{{\w+}}/.test(bodyText) || /{{\w+}}\s*$/.test(bodyText)
    }

    const getVariableDensityWarning = () => {
        const bodyText = templateForm?.content?.body?.text || ""
        const variables = getVariablesByText(bodyText)
        if (variables.length < 3) return ""

        const textWithoutVariables = bodyText.replace(/{{\w+}}/g, "").trim()
        const words = textWithoutVariables.split(/\s+/).filter(Boolean)
        return words.length < variables.length * 3
            ? "Há muitas variáveis para pouco texto. A Meta pode rejeitar modelos com excesso de variáveis em relação ao conteúdo fixo da mensagem."
            : ""
    }

    const normalizeVariableName = (name = "") => {
        return name
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .trimStart()
            .replace(/[\s_]+/g, "_")
            .replace(/[^\w]/g, "")
    }

    const normalizeNumericVariable = (name = "") => {
        return `${name}`.replace(/\D/g, "")
    }

    const isNamedVariableValid = (variable = "") => {
        return /^[a-z][a-z0-9_]*$/.test(variable) && !/^\d+$/.test(variable)
    }

    const isNumericVariableValid = (variable = "") => {
        return /^[1-9]\d*$/.test(variable)
    }

    const getVariableAnalysis = (text = "") => {
        const variables = getVariablesByText(text)
        const named = variables.filter(variable => isNamedVariableValid(variable))
        const positional = variables.filter(variable => isNumericVariableValid(variable))
        const invalid = variables.filter(variable => !isNamedVariableValid(variable) && !isNumericVariableValid(variable))
        const isMixed = named.length > 0 && positional.length > 0
        const detectedFormat = !invalid.length && !isMixed
            ? (named.length ? "NAMED" : (positional.length ? "POSITIONAL" : ""))
            : ""

        return { variables, named, positional, invalid, isMixed, detectedFormat }
    }

    const getCurrentVariableAnalysis = () => {
        return getVariableAnalysis(templateForm?.content?.body?.text || "")
    }

    const getInvalidVariableMessage = () => {
        const analysis = getCurrentVariableAnalysis()
        if (analysis.isMixed) return messageMixedVariableFormats
        if (analysis.invalid.length) return messageInvalidVariables
        return ""
    }

    const handleChangeVariableName = (value) => {
        const isNumericInput = /^\s*\d+\s*$/.test(value)
        setVariableName(isNumericInput ? normalizeNumericVariable(value) : normalizeVariableName(value))
    }

    const handleInsertEmoji = (emoji) => {
        const bodyText = templateForm?.content?.body?.text || ""
        insertTextAtCursor(bodyTextareaRef.current, bodyText, emoji, (newText) => handleChangeTemplateBodyText(newText))
    }

    const handleChangeTemplateBodyText = (newText) => {
        if (newText.length > limitsTemplateFields.body) {
            toast.error(`O corpo da mensagem pode ter até ${limitsTemplateFields.body} caracteres.`)
            return false
        }

        const detectedFormat = getVariableAnalysis(newText).detectedFormat
        setTemplateForm(previousValue => ({
            ...previousValue,
            format: detectedFormat || previousValue.format,
            content: {
                ...previousValue.content,
                body: {
                    ...previousValue.content?.body,
                    text: newText,
                },
            },
        }))
        return true
    }

    const handleLocateVariable = (variable) => {
        const bodyText = templateForm?.content?.body?.text || ""
        const token = `{{${variable}}}`
        const start = bodyText.indexOf(token)
        if (start < 0) return

        bodyTextareaRef.current?.focus()
        bodyTextareaRef.current?.setSelectionRange(start, start + token.length)
    }

    const handleInsertVariable = () => {
        const bodyText = templateForm?.content?.body?.text || ""
        const variables = getVariablesByText(bodyText)
        const detectedFormat = getVariableAnalysis(bodyText).detectedFormat
        const inputFormat = variableName
            ? (isNumericVariableValid(variableName) ? "POSITIONAL" : "NAMED")
            : (detectedFormat || templateForm?.format || "POSITIONAL")
        const normalizedName = inputFormat === "NAMED"
            ? normalizeVariableName(variableName) || `variavel_${variables.length + 1}`
            : normalizeNumericVariable(variableName) || `${variables.filter(variable => isNumericVariableValid(variable)).length + 1}`
        const variable = `{{${normalizedName}}}`
        const textarea = bodyTextareaRef.current
        const start = textarea?.selectionStart ?? bodyText.length
        const end = textarea?.selectionEnd ?? bodyText.length
        const newText = `${bodyText.substring(0, start)}${variable}${bodyText.substring(end)}`

        if (newText.length > limitsTemplateFields.body) {
            toast.error(`O corpo da mensagem pode ter até ${limitsTemplateFields.body} caracteres.`)
            return
        }

        handleChangeTemplateBodyText(newText)
        setVariableName("")

        setTimeout(() => {
            if (bodyTextareaRef.current) {
                const cursorPosition = start + variable.length
                bodyTextareaRef.current.focus()
                bodyTextareaRef.current.setSelectionRange(cursorPosition, cursorPosition)
            }
        }, 0)
    }

    const handleSaveTemplate = async (e) => {
        e.preventDefault()

        try {
            const saveAsDraft = shouldSaveTemplateAsDraft()
            if (!templateForm?.name?.trim()) {
                toast.error("O nome do template é obrigatório.")
                return
            }

            if (!saveAsDraft && !templateForm?.content?.body?.text?.trim()) {
                toast.error("O corpo do template é obrigatório.")
                return
            }

            if (!saveAsDraft && hasVariableAtEdge()) {
                toast.error(messageVariableAtEdge)
                return
            }

            const invalidVariableMessage = getInvalidVariableMessage()
            if (!saveAsDraft && invalidVariableMessage) {
                toast.error(invalidVariableMessage)
                return
            }

            const bodyRuleErrors = getTemplateBodyRuleErrors()
            if (!saveAsDraft && bodyRuleErrors.length) {
                toast.error(bodyRuleErrors[0])
                return
            }

            const buttonRuleErrors = getButtonRuleErrors()
            if (!saveAsDraft && buttonRuleErrors.length) {
                toast.error(buttonRuleErrors[0])
                return
            }

            const variablesWithoutExample = getVariablesByText(templateForm?.content?.body?.text || "").filter(variable => !getVariableExample(variable)?.trim())
            if (!saveAsDraft && variablesWithoutExample.length) {
                toast.error("Informe uma amostra para todas as variáveis do corpo da mensagem.")
                return
            }

            const headerFormat = getHeaderFormatSelected()
            const headerMediaFile = templateMedia?.[0]
            const shouldUploadHeaderMedia = mediaHeaderFormats.includes(headerFormat)
            if (shouldUploadHeaderMedia && headerMediaFile && !isValidHeaderMediaFile(headerMediaFile, headerFormat)) {
                toast.error(`Selecione um arquivo de ${getHeaderMediaConfig(headerFormat).label} para usar no cabeçalho.`)
                return
            }

            if (!saveAsDraft && shouldUploadHeaderMedia && !headerMediaFile && !templateForm?.content?.header?.handle) {
                toast.error("Selecione uma mídia de exemplo para o cabeçalho antes de salvar o template.")
                return
            }

            const copyCodeButtonWithoutExample = templateForm?.content?.buttons?.find(button => button?.type === "COPY_CODE" && !button?.copy_code_example && !button?.examples?.[0])
            if (!saveAsDraft && copyCodeButtonWithoutExample) {
                toast.error("Informe uma amostra para o botão de copiar código.")
                return
            }

            setIsLoading(true)
            let headerMediaHandle = templateForm?.content?.header?.handle || ""
            if (shouldUploadHeaderMedia && headerMediaFile) {
                headerMediaHandle = await uploadTemplateMediaFile(headerMediaFile)
                if (!headerMediaHandle) {
                    toast.error("Não foi possível preparar a mídia para análise da Meta.")
                    return
                }
            }

            const payload = getTemplatePayloadByForm(shouldUploadHeaderMedia ? {
                header: {
                    handle: headerMediaHandle,
                    file: undefined,
                },
            } : {})
            const targetWabaId = payload?.waba_id || selectedWabaId
            if (saveAsDraft) {
                const responseData = await saveLocalTemplateDraft(payload)
                const [savedDraft] = responseData?.data || [{
                    ...payload,
                    status: "DRAFT",
                    is_local_draft: true,
                }]
                const targetTemplates = templatesByWaba[targetWabaId] || (targetWabaId === selectedWabaId ? templates : [])
                const updatedTargetTemplates = [
                    savedDraft,
                    ...targetTemplates.filter(template => template?.template_id !== savedDraft?.template_id),
                ]

                setTemplatesByWaba(previousValue => ({
                    ...previousValue,
                    [targetWabaId]: updatedTargetTemplates,
                }))

                if (targetWabaId === selectedWabaId) {
                    setTemplates(updatedTargetTemplates)
                } else {
                    setSelectedWabaId(targetWabaId)
                    setTemplates(updatedTargetTemplates)
                    setTemplatesLoaded(true)
                }

                setTemplateMedia([])
                toast.success(responseData?.message || "Rascunho salvo com sucesso!")
                handleCloseModalTemplateForm()
                return
            }

            const response = templateFormMode === "edit"
                ? await api.patch(`/integrations/whatsapp-api/templates/${payload?.template_id}`, payload)
                : await api.post("/integrations/whatsapp-api/templates", payload)

            const savedTemplate = {
                ...payload,
                ...(response?.data?.data || {}),
                inbox_id: payload?.inbox_id,
                waba_id: targetWabaId,
            }
            const targetTemplates = templatesByWaba[targetWabaId] || (targetWabaId === selectedWabaId ? templates : [])
            if (`${payload?.template_id}`.startsWith("local-")) {
                try {
                    await deleteLocalTemplateDraft(payload)
                } catch (err) {
                    if (err?.response?.status !== 404) throw err
                }
            }

            const templateExists = targetTemplates.some(template => template?.template_id === savedTemplate?.template_id)
            const updatedTargetTemplates = templateExists
                ? targetTemplates.map(template => template?.template_id === savedTemplate?.template_id ? savedTemplate : template)
                : [savedTemplate, ...targetTemplates.filter(template => template?.template_id !== payload?.template_id)]

            setTemplatesByWaba(previousValue => ({
                ...previousValue,
                [targetWabaId]: updatedTargetTemplates,
            }))

            if (targetWabaId === selectedWabaId) {
                setTemplates(updatedTargetTemplates)
            } else {
                setSelectedWabaId(targetWabaId)
                setTemplates(updatedTargetTemplates)
            }

            setTemplatesLoaded(true)
            setTemplateMedia([])
            toast.success(response?.data?.message || "Template salvo com sucesso!")
            handleCloseModalTemplateForm()
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleDeleteTemplate = async (templateId) => {
        try {
            const template = templates.find(template => template?.template_id === templateId)
            if (!template) {
                toast.error("Template não encontrado.")
                return
            }

            if (`${templateId}`.startsWith("local-")) {
                try {
                    await deleteLocalTemplateDraft(template)
                } catch (err) {
                    if (err?.response?.status !== 404) throw err
                }
                const updatedTemplates = templates.filter(template => template?.template_id !== templateId)
                setTemplates(updatedTemplates)
                setTemplatesByWaba(previousValue => ({
                    ...previousValue,
                    [selectedWabaId]: updatedTemplates,
                }))
                setSelectedTemplateIds(previousValue => previousValue.filter(id => id !== templateId))
                toast.success("Rascunho excluído com sucesso!")
                handleCloseDeleteTemplate()
                return
            }

            setIsLoading(true)
            const response = await api.delete(`/integrations/whatsapp-api/templates/${templateId}`, {
                params: {
                    waba_id: selectedWabaId,
                    name: template?.name,
                }
            })

            const updatedTemplates = templates.filter(template => template?.template_id !== templateId)
            setTemplates(updatedTemplates)
            setTemplatesByWaba(previousValue => ({
                ...previousValue,
                [selectedWabaId]: updatedTemplates,
            }))
            toast.success(response?.data?.message || "Template excluído com sucesso!")
            handleCloseDeleteTemplate()
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const getHeaderFormatSelected = () => {
        return templateForm?.content?.header?.format || "NONE"
    }

    const canSaveTemplateAsDraft = () => {
        return templateFormMode !== "edit"
    }

    const shouldSaveTemplateAsDraft = () => {
        return canSaveTemplateAsDraft() && templateSubmitMode === "DRAFT"
    }

    const getTemplateSubmitButtonText = () => {
        if (shouldSaveTemplateAsDraft()) return "Salvar rascunho"
        return templateFormMode === "edit" ? "Salvar alterações" : "Enviar para análise"
    }

    const getPreviewContent = () => {
        const headerMediaFile = templateMedia?.[0]
        const payloadContent = getTemplatePayloadByForm(headerMediaFile ? {
            header: {
                file: headerMediaFile,
                filename: headerMediaFile?.name,
            },
        } : {})?.content || {}
        const bodyText = payloadContent?.body?.text || ""
        const previewBodyText = bodyText.replace(/{{(\w+)}}/g, (match, variable) => getVariableExample(variable) || match)

        return {
            ...payloadContent,
            body: {
                ...payloadContent.body,
                text: previewBodyText,
            },
            buttons: templateForm?.content?.buttons || [],
        }
    }

    const deleteLocalTemplateDraft = async (template) => {
        return api.delete(`/integrations/whatsapp-api/templates/drafts/${template?.template_id}`, {
            params: {
                inbox_id: template?.inbox_id,
                waba_id: template?.waba_id || selectedWabaId,
            },
        })
    }

    const saveLocalTemplateDraft = async (payload) => {
        const targetWabaId = payload?.waba_id || selectedWabaId
        const response = await api.post("/integrations/whatsapp-api/templates/drafts", {
            inbox_id: payload?.inbox_id || getRepresentativeInboxIdByWaba(targetWabaId),
            waba_id: targetWabaId,
            templates: [{
                ...payload,
                status: "DRAFT",
                is_local_draft: true,
            }],
        })

        return response?.data
    }

    const handleLibraryDraftAdded = (draft, wabaId) => {
        const targetTemplates = templatesByWaba[wabaId] || (wabaId === selectedWabaId ? templates : [])
        const updatedTemplates = [draft, ...targetTemplates.filter(template => template?.template_id !== draft?.template_id)]
        setTemplatesByWaba(previousValue => ({ ...previousValue, [wabaId]: updatedTemplates }))
        if (wabaId === selectedWabaId) {
            setTemplates(updatedTemplates)
            setTemplatesLoaded(true)
        }
    }

    const getLibraryExistingTemplates = async (wabaId) => {
        if (templatesByWaba[wabaId]) return templatesByWaba[wabaId]

        const response = await api.get(`/integrations/whatsapp-api/templates/${wabaId}`)
        const formattedTemplates = (response?.data?.data || []).map(template => ({
            ...template,
            status: template?.status || "APPROVED",
            inbox_id: template?.inbox_id || getRepresentativeInboxIdByWaba(wabaId),
            waba_id: wabaId,
        }))
        setTemplatesByWaba(previousValue => ({ ...previousValue, [wabaId]: formattedTemplates }))
        return formattedTemplates
    }

    return (
        <div className={styles.templates}>
            <div className={styles.templateSectionTabs}>
                <button type="button" className={activeSection === "account" ? styles.active : ""} onClick={() => setActiveSection("account")}>
                    Meus templates
                </button>
                <button type="button" className={activeSection === "library" ? styles.active : ""} onClick={() => setActiveSection("library")} disabled={Boolean(bulkDraftProgress)}>
                    <HiOutlineTemplate /> Modelos de templates
                </button>
            </div>
            {activeSection === "library" ?
                <TemplateLibrary
                    inboxOptions={whatsAppInboxOptions}
                    existingTemplatesByWaba={templatesByWaba}
                    getExistingTemplates={getLibraryExistingTemplates}
                    onDraftAdded={handleLibraryDraftAdded}
                    setIsLoading={setIsLoading}
                />
                : <>
            <div className={styles.templateActions}>
                <Input
                    elementRef={importTemplatesInputRef}
                    className={styles.hiddenFileInput}
                    type="file"
                    accept="application/json,.json"
                    onChange={handleImportTemplatesFile}
                />
                <Button variant="secondary" onClick={synchronizeTemplates} disabled={!selectedWabaId || Boolean(bulkDraftProgress) || isSynchronizing}>
                    <MdSync /> {isSynchronizing ? "Sincronizando..." : "Sincronizar"}
                </Button>
                <Button variant="secondary" onClick={handleExportTemplates} disabled={!selectedWabaId || !templates.length || Boolean(bulkDraftProgress)}>
                    <MdOutlineFileDownload /> Exportar JSON
                </Button>
                <Button variant="secondary" onClick={handleOpenImportTemplates} disabled={!selectedWabaId || Boolean(bulkDraftProgress)}>
                    <MdOutlineFileUpload /> Importar JSON
                </Button>
                <Button onClick={handleToggleDuplicateMode} variant={duplicateMode ? "primary" : "secondary"} disabled={!selectedWabaId || !templates.length || Boolean(bulkDraftProgress)}>
                    <MdContentCopy /> {duplicateMode ? "Cancelar duplicação" : "Duplicar templates"}
                </Button>
                <Button onClick={handleToggleDraftSubmitMode} variant={draftSubmitMode ? "primary" : "secondary"} disabled={!selectedWabaId || !templates.some(isTemplateDraft) || Boolean(bulkDraftProgress)}>
                    <HiOutlineTemplate /> {draftSubmitMode ? "Cancelar gerenciamento" : "Gerenciar rascunhos"}
                </Button>
                <Button variant="primary" onClick={handleOpenNewTemplate} disabled={!selectedWabaId || Boolean(bulkDraftProgress)}>
                    <MdAdd /> Novo template
                </Button>
            </div>
            <div className={styles.templateToolbar}>
                <Select name="Conta do WhatsApp" placeholder="Buscar conta..." options={whatsAppAccountsOptions} selected={selectedWabaId} onChange={handleChangeTemplateWaba} disabled={!whatsAppAccountsOptions.length || Boolean(bulkDraftProgress)} />
                <Select multiple={true} name="Status" placeholder="Buscar status..." options={optionsTemplateStatus} selected={filtersTemplates.status || []} onChange={(value) => handleChangeTemplateFilter("status", value)} />
                <Select multiple={true} name="Categoria" placeholder="Buscar categoria..." options={optionsTemplateCategory} selected={filtersTemplates.category || []} onChange={(value) => handleChangeTemplateFilter("category", value)} />
                <Select multiple={true} name="Idioma" placeholder="Buscar idioma..." options={templateLanguageOptions} selected={filtersTemplates.language || []} onChange={(value) => handleChangeTemplateFilter("language", value)} disabled={!templateLanguageOptions.length} />
                <Input type="search" placeholder="Buscar template..." value={filtersTemplates.textSearch || ""} onChange={(e) => handleChangeTemplateFilter("textSearch", e.target.value)} />
            </div>
            <div className={styles.templateSummary}>
                <div>
                    <strong>{templates.length}</strong>
                    <span>templates sincronizados</span>
                </div>
                <div>
                    <strong>{getFilteredTemplateItems().length}</strong>
                    <span>templates na visualização</span>
                </div>
                <div>
                    <strong>{getTemplateAnalytics().approved}</strong>
                    <span>aprovados</span>
                </div>
                <div>
                    <strong>{getTemplateAnalytics().pending}</strong>
                    <span>em análise</span>
                </div>
                <div>
                    <strong>{getTemplateAnalytics().rejected}</strong>
                    <span>rejeitados</span>
                </div>
                <button onClick={handleOpenTemplatesManager} disabled={!selectedWabaId}>
                    <MdOpenInNew /> Abrir gerenciador da Meta
                </button>
            </div>
            <div className={styles.templateViewSwitcher}>
                <span>Visualização</span>
                <div role="group" aria-label="Modo de visualização dos templates">
                    <button type="button" className={templateViewMode === "table" ? styles.active : ""} onClick={() => setTemplateViewMode("table")} aria-pressed={templateViewMode === "table"} title="Visualizar em tabela">
                        <MdViewList /> Tabela
                    </button>
                    <button type="button" className={templateViewMode === "cards" ? styles.active : ""} onClick={() => setTemplateViewMode("cards")} aria-pressed={templateViewMode === "cards"} title="Visualizar em cards">
                        <MdViewModule /> Cards
                    </button>
                </div>
            </div>
            {duplicateMode &&
                <div className={styles.bulkActions}>
                    <div>
                        <strong>{selectedTemplateIds.length}</strong>
                        <span>template{selectedTemplateIds.length === 1 ? "" : "s"} selecionado{selectedTemplateIds.length === 1 ? "" : "s"}</span>
                    </div>
                    <Button variant="primary" type="button" onClick={handleOpenDuplicateTemplatesModal} disabled={!selectedTemplateIds.length}>
                        <MdContentCopy /> Continuar duplicação
                    </Button>
                    <Button variant="secondary" type="button" onClick={handleToggleDuplicateMode}>
                        Cancelar
                    </Button>
                </div>
            }
            {draftSubmitMode &&
                <div className={styles.bulkActions}>
                    {!bulkDraftProgress &&
                        <div>
                            <strong>{selectedTemplateIds.length}</strong>
                            <span>rascunho{selectedTemplateIds.length === 1 ? "" : "s"} selecionado{selectedTemplateIds.length === 1 ? "" : "s"}</span>
                        </div>
                    }
                    {bulkDraftProgress ?
                        <div className={styles.bulkProgress}>
                            <div>
                                <strong>{bulkDraftProgress.action}</strong>
                                <span>{bulkDraftProgress.current} de {bulkDraftProgress.total} · {Math.round((bulkDraftProgress.current / Math.max(bulkDraftProgress.total, 1)) * 100)}%</span>
                            </div>
                            <div className={styles.bulkProgressTrack} role="progressbar" aria-label={bulkDraftProgress.action} aria-valuemin="0" aria-valuemax={bulkDraftProgress.total} aria-valuenow={bulkDraftProgress.current}>
                                <span style={{ width: `${(bulkDraftProgress.current / Math.max(bulkDraftProgress.total, 1)) * 100}%` }} />
                            </div>
                            <small>{bulkDraftProgress.currentName || "Preparando próximo template..."}</small>
                        </div>
                        : <>
                            <Button variant="primary" type="button" onClick={handleOpenSelectedDraftsSubmissionConfirmation} disabled={!selectedTemplateIds.length}>
                                <MdSend /> Enviar para análise
                            </Button>
                            <Button variant="primary" type="button" onClick={handleOpenDeleteSelectedDrafts} disabled={!selectedTemplateIds.length}>
                                <MdOutlineDeleteOutline /> Excluir rascunhos
                            </Button>
                            <Button variant="secondary" type="button" onClick={handleToggleAllVisibleDrafts} disabled={!getVisibleDraftIds().length}>
                                {getVisibleDraftIds().length > 0 && getVisibleDraftIds().every(id => selectedTemplateIds.includes(id)) ? "Desmarcar todos" : "Selecionar todos"}
                            </Button>
                            <Button variant="secondary" type="button" onClick={handleCloseDraftSubmitMode}>
                                Cancelar
                            </Button>
                        </>
                    }
                </div>
            }
            {templateViewMode === "cards" ? renderTemplateCards() :
                <Table
                    headers={headersTemplates}
                    values={getFilteredTemplates()}
                    className={bulkDraftProgress ? styles.tableProcessing : ""}
                    messageNotRegisters={templatesLoaded ? "Nenhum template encontrado" : "Selecione uma conta e sincronize os templates"}
                    onClick={duplicateMode ? handleToggleTemplateSelection : (draftSubmitMode ? handleToggleDraftTemplateSelection : handleShowModalTemplate)}
                    showCount={true}
                    count={getFilteredTemplateItems()?.length}
                />
            }
            {showModalImportTemplates &&
                <Modal
                    icon={<MdOutlineFileUpload />}
                    title="Importar templates"
                    description={importFileName || "Revise os templates antes de importar"}
                    closeModal={handleCloseImportTemplatesModal}
                    fitContent={true}
                    heightFitContent={true}
                >
                    <div className={styles.importModal}>
                        <div className={styles.importConfig}>
                            <div className={styles.importSummary}>
                                <div>
                                    <span>Origem</span>
                                    <strong>{importSource?.waba_name || importSource?.waba_id || "Arquivo JSON"}</strong>
                                </div>
                                <div>
                                    <span>Templates no arquivo</span>
                                    <strong>{importTemplates.length}</strong>
                                </div>
                                <div>
                                    <span>Selecionados</span>
                                    <strong>{selectedImportTemplateIds.length}</strong>
                                </div>
                            </div>
                            <div className={styles.importOptions}>
                                <FormGroup labelText={labelWithInfo("Caixa de entrada de destino", "Os templates serão salvos como rascunhos nesta caixa, usando a conta WhatsApp selecionada.")}>
                                    <Select
                                        name="Caixa de entrada"
                                        placeholder="Buscar caixa..."
                                        options={whatsAppInboxOptions.filter(option => option?.waba_id === selectedWabaId)}
                                        selected={importTargetInboxId}
                                        onChange={setImportTargetInboxId}
                                        disabled={!whatsAppInboxOptions.length}
                                        showOptionAll={false}
                                        allowDeselectAll={false}
                                    />
                                </FormGroup>
                                <div className={styles.duplicateStatusOptions}>
                                    <span>{labelWithInfo("Nomes dos templates", "Escolha como lidar com nomes que já existem na conta de destino.")}</span>
                                    <label className={importNameStrategy === "RENAME" ? styles.active : ""} onClick={() => setImportNameStrategy("RENAME")}>
                                        <span className={styles.optionIndicator} />
                                        <div>
                                            <strong>Renomear conflitos</strong>
                                            <small>Adiciona sufixos quando o nome já estiver em uso.</small>
                                        </div>
                                    </label>
                                    <label className={importNameStrategy === "KEEP" ? styles.active : ""} onClick={() => setImportNameStrategy("KEEP")}>
                                        <span className={styles.optionIndicator} />
                                        <div>
                                            <strong>Manter nomes</strong>
                                            <small>Preserva exatamente os nomes do arquivo importado.</small>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            <div className={styles.importListHeader}>
                                <button type="button" onClick={handleToggleAllImportTemplates}>
                                    {selectedImportTemplateIds.length === importTemplates.length ? "Desmarcar todos" : "Selecionar todos"}
                                </button>
                                <span>Os templates importados ficam como rascunho para revisão.</span>
                            </div>
                            <div className={styles.importTemplateList}>
                                {importTemplates.map(template => (
                                    <button
                                        key={template.import_id}
                                        type="button"
                                        className={selectedImportTemplateId === template.import_id ? styles.active : ""}
                                        onClick={() => setSelectedImportTemplateId(template.import_id)}
                                    >
                                        <span
                                            className={`${styles.selectionBox} ${selectedImportTemplateIds.includes(template.import_id) ? styles.checked : ""}`}
                                            onClick={(e) => { e.stopPropagation(); handleToggleImportTemplateSelection(template.import_id) }}
                                            aria-hidden="true"
                                        />
                                        <div>
                                            <strong>{template?.name || "-"}</strong>
                                            <span>{getTemplateCategoryLabel(template?.category)} - {template?.language || "-"}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className={styles.importPreview}>
                            <div className={styles.sectionHeader}>
                                <strong>{getSelectedImportTemplatePreview()?.name || "Template"}</strong>
                                <span>{getTemplateCategoryLabel(getSelectedImportTemplatePreview()?.category)} - {getSelectedImportTemplatePreview()?.language || "-"}</span>
                            </div>
                            <WhatsAppTemplatePreview template={getSelectedImportTemplatePreview()} />
                        </div>
                        <div className={styles.importActions}>
                            <Button variant="secondary" type="button" onClick={handleCloseImportTemplatesModal}>Cancelar</Button>
                            <Button variant="primary" type="button" onClick={handleConfirmImportTemplates} disabled={!selectedImportTemplateIds.length || !importTargetInboxId}>
                                <MdOutlineFileUpload /> Importar como rascunho
                            </Button>
                        </div>
                    </div>
                </Modal>
            }
            {showModalDuplicateTemplates &&
                <Modal
                    icon={<MdContentCopy />}
                    title="Duplicar templates"
                    description="Escolha para onde os templates serão copiados e como eles devem ficar."
                    closeModal={handleCloseDuplicateTemplatesModal}
                    heightFitContent={true}
                >
                    <form className={styles.duplicateModal} onSubmit={(e) => { e.preventDefault(); handleDuplicateSelectedTemplates() }}>
                        <div className={styles.duplicateSummary}>
                            <strong>{selectedTemplateIds.length} template{selectedTemplateIds.length === 1 ? "" : "s"} selecionado{selectedTemplateIds.length === 1 ? "" : "s"}</strong>
                            <span>
                                {getSelectedTemplatesToDuplicate().slice(0, 3).map(template => template?.name).filter(Boolean).join(", ")}
                                {selectedTemplateIds.length > 3 ? ` e mais ${selectedTemplateIds.length - 3}` : ""}
                            </span>
                        </div>
                        <FormGroup labelText={labelWithInfo("Caixa de entrada de destino", "Escolha em qual número ou caixa esses modelos devem ficar disponíveis.")}>
                            <Select
                                name="Caixa de entrada"
                                placeholder="Buscar caixa..."
                                options={whatsAppInboxOptions}
                                selected={duplicateTargetInboxId}
                                onChange={handleChangeDuplicateTargetInbox}
                                disabled={!whatsAppInboxOptions.length}
                                showOptionAll={false}
                            />
                        </FormGroup>
                        <div className={styles.duplicateStatusOptions}>
                            <span>{labelWithInfo("Como salvar", "Escolha se quer revisar depois ou enviar agora para análise da Meta.")}</span>
                            <label className={duplicateSubmitMode === "DRAFT" ? styles.active : ""} onClick={() => setDuplicateSubmitMode("DRAFT")}>
                                <span className={styles.optionIndicator} />
                                <div>
                                    <strong>Salvar como rascunho</strong>
                                    <small>Cria cópias para revisar antes de enviar.</small>
                                </div>
                            </label>
                            <label className={duplicateSubmitMode === "SUBMIT" ? styles.active : ""} onClick={() => setDuplicateSubmitMode("SUBMIT")}>
                                <span className={styles.optionIndicator} />
                                <div>
                                    <strong>Enviar para aprovação</strong>
                                    <small>Envia as cópias direto para análise da Meta.</small>
                                </div>
                            </label>
                        </div>
                        <div className={styles.duplicateActions}>
                            <Button variant="secondary" type="button" onClick={handleCloseDuplicateTemplatesModal}>Voltar</Button>
                            <Button variant="primary" type="submit" disabled={!duplicateTargetInboxId}>
                                <MdContentCopy /> {duplicateSubmitMode === "SUBMIT" ? "Enviar para aprovação" : "Criar rascunhos"}
                            </Button>
                        </div>
                    </form>
                </Modal>
            }
            {showModalTemplate &&
                <Modal
                    icon={<HiOutlineTemplate />}
                    title={selectedTemplate?.name || "Template"}
                    description={`${getTemplateCategoryLabel(selectedTemplate?.category)} - ${selectedTemplate?.language || "Idioma não informado"}`}
                    actions={isTemplateDraft(selectedTemplate) ? [
                        { label: "Editar", onClick: () => handleOpenEditTemplate(selectedTemplate?.template_id) },
                        { label: "Enviar para análise", onClick: () => handleOpenDraftSubmissionConfirmation([selectedTemplate], "single") },
                    ] : [
                        { label: "Editar", onClick: () => handleOpenEditTemplate(selectedTemplate?.template_id) },
                        { label: "Abrir na Meta", onClick: handleOpenTemplatesManager },
                    ]}
                    closeModal={handleCloseModalTemplate}
                    heightFitContent={true}
                >
                    <div className={styles.templateModal}>
                        <div className={styles.templateDetails}>
                            <div>
                                <span>Status</span>
                                <strong>{getTemplateStatusLabel(selectedTemplate?.status || "APPROVED")}</strong>
                            </div>
                            <div>
                                <span>Categoria</span>
                                <strong>{getTemplateCategoryLabel(selectedTemplate?.category)}</strong>
                            </div>
                            <div>
                                <span>Idioma</span>
                                <strong>{selectedTemplate?.language || "-"}</strong>
                            </div>
                            <div>
                                <span>Formato</span>
                                <strong>{selectedTemplate?.format || "-"}</strong>
                            </div>
                            <div>
                                <span>Criação</span>
                                <strong>{formatTemplateCreatedAt(selectedTemplate, true)}</strong>
                            </div>
                        </div>
                        <WhatsAppTemplatePreview template={selectedTemplate} time="12:45" />
                        {selectedTemplate?.content?.body?.parameters?.length > 0 &&
                            <div className={styles.templateVariables}>
                                <span>Variáveis</span>
                                {selectedTemplate.content.body.parameters.map((parameter, index) => (
                                    <div key={index}>
                                        <strong>{parameter?.name}</strong>
                                        <span>{parameter?.example || "-"}</span>
                                    </div>
                                ))}
                            </div>
                        }
                    </div>
                </Modal>
            }
            {showModalTemplateForm &&
                <Modal
                    icon={<HiOutlineTemplate />}
                    title={templateFormMode === "edit" ? "Editar template" : (templateFormMode === "duplicate" ? "Duplicar template" : "Novo template")}
                    description="Configure o conteúdo antes de enviar para aprovação"
                    fullWidith={true}
                    heightFitContent={true}
                    closeModal={handleCloseModalTemplateForm}
                >
                    <form className={styles.templateFormShell} onSubmit={handleSaveTemplate}>
                        <div className={styles.templateForm}>
                            <div className={styles.templateFormFields}>
                                <div className={styles.formSection}>
                                    <div className={styles.sectionHeader}>
                                        <strong>Identificação</strong>
                                        <span>Escolha onde o modelo será usado e qual é o objetivo da mensagem.</span>
                                    </div>
                                    <FormGroup labelText={labelWithInfo(templateFormMode === "duplicate" ? "Duplicar para a conta do WhatsApp" : "Conta do WhatsApp do template", "Escolha em qual conta este modelo ficará disponível para envio.")}>
                                        <Select
                                            name="Conta do WhatsApp"
                                            placeholder="Buscar conta..."
                                            options={whatsAppAccountsOptions}
                                            selected={templateForm?.waba_id || selectedWabaId}
                                            onChange={(value) => {
                                                handleChangeTemplateForm("waba_id", value)
                                                handleChangeTemplateForm("inbox_id", getRepresentativeInboxIdByWaba(value))
                                            }}
                                            disabled={templateFormMode === "edit"}
                                            showOptionAll={false}
                                        />
                                    </FormGroup>
                                    <div className={styles.identificationGrid}>
                                        <FormGroup labelText={labelWithInfo("Nome do modelo", "Use um nome fácil de reconhecer depois, como confirmacao_agendamento.")} rightContent={getCounter(templateForm?.name, limitsTemplateFields.name)}>
                                            <Input value={templateForm?.name || ""} placeholder="ex: confirmacao_agendamento" onChange={(e) => handleChangeTemplateForm("name", e.target.value)} required={true} maxLength={limitsTemplateFields.name} />
                                        </FormGroup>
                                        <FormGroup labelText={labelWithInfo("Idioma", "Escolha o idioma em que a mensagem será escrita.")}>
                                            <Select name="Idioma" placeholder="Buscar idioma..." options={optionsTemplateLanguage} selected={templateForm?.language} onChange={(value) => handleChangeTemplateForm("language", value)} showOptionAll={false} allowDeselectAll={false} />
                                        </FormGroup>
                                    </div>
                                    <div className={styles.templateMetaGrid}>
                                        <FormGroup labelText={labelWithInfo("Finalidade", "Templates de solicitação de chamada exibem o controle nativo para o cliente autorizar ou recusar ligações.")}>
                                            <Select
                                                name="Finalidade"
                                                options={optionsTemplatePurpose}
                                                selected={templateForm?.content?.call_permission_request ? "CALL_PERMISSION_REQUEST" : "STANDARD"}
                                                onChange={handleChangeTemplatePurpose}
                                                showOptionAll={false}
                                                allowDeselectAll={false}
                                            />
                                        </FormGroup>
                                        <FormGroup labelText={labelWithInfo("Categoria", "Escolha o objetivo da mensagem: aviso, divulgação ou código de acesso.")}>
                                            <Select name="Categoria" options={optionsTemplateCategory} selected={templateForm?.category} onChange={(value) => handleChangeTemplateForm("category", value)} showOptionAll={false} disabled={templateForm?.content?.call_permission_request} />
                                        </FormGroup>
                                        {canSaveTemplateAsDraft() ?
                                            <FormGroup labelText={labelWithInfo("Status", "Escolha se quer guardar para revisar depois ou enviar agora para a análise da Meta.")}>
                                                <Select name="Status" options={optionsTemplateSubmitMode} selected={templateSubmitMode} onChange={setTemplateSubmitMode} showOptionAll={false} allowDeselectAll={false} />
                                            </FormGroup>
                                            :
                                            <FormGroup labelText={labelWithInfo("Status", "Mostra se o modelo já pode ser usado ou se ainda está em análise.")}>
                                                <div className={styles.statusReadOnly}>
                                                    <span>{getTemplateStatusLabel(templateForm?.status)}</span>
                                                </div>
                                            </FormGroup>
                                        }
                                    </div>
                                </div>
                                <div className={styles.formSection}>
                                    <div className={styles.sectionHeader}>
                                        <strong>Mensagem</strong>
                                        <span>Escreva a mensagem que será enviada aos clientes.</span>
                                    </div>
                                    {templateForm?.content?.call_permission_request &&
                                        <div className={styles.templateApiNotice}>
                                            <strong>Solicitação de chamada</strong>
                                            <span>A Meta adicionará o controle nativo de autorização. Para maior compatibilidade, use uma mensagem curta, contextual e sem variáveis ou outros botões.</span>
                                        </div>
                                    }
                                    {!templateForm?.content?.call_permission_request &&
                                        <>
                                            <FormGroup labelText={labelWithInfo("Cabeçalho", "Parte opcional que aparece antes da mensagem principal.")}>
                                                <Select name="Formato do cabeçalho" options={optionsHeaderFormat} selected={getHeaderFormatSelected()} onChange={handleChangeHeaderFormat} showOptionAll={false} />
                                            </FormGroup>
                                            {getHeaderFormatSelected() === "TEXT" &&
                                                <FormGroup labelText={labelWithInfo("Texto do cabeçalho", "Um título curto para abrir a mensagem.")} rightContent={getCounter(templateForm?.content?.header?.text, limitsTemplateFields.header)}>
                                                    <Input value={templateForm?.content?.header?.text || ""} placeholder="ex: Confirmação de agendamento" onChange={(e) => handleChangeTemplateContent("header", "text", e.target.value)} maxLength={limitsTemplateFields.header} />
                                                </FormGroup>
                                            }
                                            {mediaHeaderFormats.includes(getHeaderFormatSelected()) &&
                                                <div className={styles.mediaUploadBox}>
                                                    <FormGroup labelText={labelWithInfo("Mídia de exemplo", "Escolha o arquivo que aparece no cabeçalho da mensagem. Ele será enviado para a Meta junto com o template, porque a aprovação precisa dessa amostra.")}>
                                                        <InputFile value={templateMedia} onChange={handleChangeTemplateMedia} accept={getHeaderMediaConfig().accept} />
                                                    </FormGroup>
                                                    <span className={templateMedia?.[0] || templateForm?.content?.header?.handle ? styles.mediaUploadSuccess : styles.mediaUploadHint}>
                                                        {templateMedia?.[0]
                                                            ? "A prévia já mostra essa mídia. Ela será enviada quando você salvar o template."
                                                            : (templateForm?.content?.header?.handle ? "Este template já tem uma mídia de exemplo salva." : `Selecione um arquivo de ${getHeaderMediaConfig().label}.`)}
                                                    </span>
                                                </div>
                                            }
                                        </>
                                    }
                                    <div className={styles.messageBuilder}>
                                        <div className={styles.messageBuilderHeader}>
                                            <div>
                                                <strong>{labelWithInfo("Corpo da mensagem", "Texto principal que o cliente vai receber. Use variáveis para dados como nome, data ou pedido.")}</strong>
                                                {getCounter(templateForm?.content?.body?.text, limitsTemplateFields.body)}
                                            </div>
                                            <span>Use variáveis para personalizar dados como nome, data ou horário.</span>
                                        </div>
                                        <RichTextarea
                                            textareaRef={bodyTextareaRef}
                                            className={getInvalidVariableMessage() ? styles.richTextareaError : ""}
                                            value={templateForm?.content?.body?.text || ""}
                                            placeholder="Ex: Olá {{nome}}, seu agendamento foi confirmado para {{data}} às {{horario}}. Responda esta mensagem se precisar alterar."
                                            onChange={handleChangeTemplateBodyText}
                                            minRows={7}
                                            maxRows={10}
                                            toolbarRef={emojiPickerRef}
                                            textareaProps={{
                                                required: !shouldSaveTemplateAsDraft(),
                                                maxLength: limitsTemplateFields.body,
                                            }}
                                            toolbarActions={
                                                <>
                                                    <button type="button" title="Emoji" onMouseDown={(e) => e.preventDefault()} onClick={() => setShowEmojiPicker(previousValue => !previousValue)}>
                                                        <MdOutlineEmojiEmotions />
                                                    </button>
                                                    {showEmojiPicker &&
                                                        <div className={styles.emojiPickerPanel}>
                                                            <Emojis onClick={handleInsertEmoji} />
                                                        </div>
                                                    }
                                                </>
                                            }
                                        />
                                        {countTemplateEmojis(templateForm?.content?.body?.text) > 0 &&
                                            <span className={countTemplateEmojis(templateForm?.content?.body?.text) > limitsTemplateFields.emojis ? styles.variableError : styles.variableNotice}>
                                                Emojis no corpo: {countTemplateEmojis(templateForm?.content?.body?.text)}/{limitsTemplateFields.emojis}
                                            </span>
                                        }
                                        {getTemplateBodyRuleErrors().map((message, index) => (
                                            <span className={styles.variableError} key={`body-rule-${index}`}>{message}</span>
                                        ))}
                                        {!templateForm?.content?.call_permission_request &&
                                        <div className={styles.variableBuilder}>
                                            <div className={styles.variableBuilderHeader}>
                                                <strong>Variáveis</strong>
                                            </div>
                                            <div className={styles.variableCreator}>
                                                <Input value={variableName} placeholder="ex: nome_cliente ou 1" onChange={(e) => handleChangeVariableName(e.target.value)} />
                                                <Button variant="secondary" type="button" onClick={handleInsertVariable}>
                                                    <MdAdd /> Inserir variável
                                                </Button>
                                            </div>
                                            {getInvalidVariableMessage() &&
                                                <div className={styles.variableConflict}>
                                                    <strong>{getInvalidVariableMessage()}</strong>
                                                    <span>Clique em uma variável para localizá-la na mensagem.</span>
                                                    <div className={styles.variableConflictGroups}>
                                                        {getCurrentVariableAnalysis().named.length > 0 &&
                                                            <div>
                                                                <span>Com nome</span>
                                                                <div>
                                                                    {getCurrentVariableAnalysis().named.map(variable => (
                                                                        <button key={`named-${variable}`} type="button" onClick={() => handleLocateVariable(variable)}>{`{{${variable}}}`}</button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        }
                                                        {getCurrentVariableAnalysis().positional.length > 0 &&
                                                            <div>
                                                                <span>Numeradas</span>
                                                                <div>
                                                                    {getCurrentVariableAnalysis().positional.map(variable => (
                                                                        <button key={`positional-${variable}`} type="button" onClick={() => handleLocateVariable(variable)}>{`{{${variable}}}`}</button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        }
                                                        {getCurrentVariableAnalysis().invalid.length > 0 &&
                                                            <div>
                                                                <span>Formato inválido</span>
                                                                <div>
                                                                    {getCurrentVariableAnalysis().invalid.map(variable => (
                                                                        <button key={`invalid-${variable}`} type="button" onClick={() => handleLocateVariable(variable)}>{`{{${variable}}}`}</button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                            }
                                            {hasVariableAtEdge() &&
                                                <span className={styles.variableError}>{messageVariableAtEdge}</span>
                                            }
                                            {getDuplicatedVariables().length > 0 &&
                                                <span className={styles.variableNotice}>
                                                    Variáveis repetidas detectadas: {getDuplicatedVariables().map(variable => `{{${variable}}}`).join(", ")}. A amostra será solicitada apenas uma vez para cada variável.
                                                </span>
                                            }
                                            {getVariableDensityWarning() &&
                                                <span className={styles.variableNotice}>{getVariableDensityWarning()}</span>
                                            }
                                            {getVariablesByText(templateForm?.content?.body?.text).length > 0 &&
                                                <div className={styles.variableSamples}>
                                                    <div className={styles.variableSamplesHeader}>
                                                        <strong>Amostras de variáveis</strong>
                                                        <span>Preencha exemplos para vermos como a mensagem ficará. Use dados fictícios.</span>
                                                    </div>
                                                    {getVariablesByText(templateForm?.content?.body?.text).map(variable => (
                                                        <FormGroup key={variable} labelText={`Insira conteúdo para {{${variable}}}`}>
                                                            <Input value={getVariableExample(variable)} placeholder={`Amostra para {{${variable}}}`} onChange={(e) => handleChangeVariableExample(variable, e.target.value)} />
                                                        </FormGroup>
                                                    ))}
                                                </div>
                                            }
                                        </div>
                                        }
                                    </div>
                                    {!templateForm?.content?.call_permission_request &&
                                        <FormGroup labelText={labelWithInfo("Rodapé", "Texto curto opcional no fim da mensagem.")} rightContent={getCounter(templateForm?.content?.footer?.text, limitsTemplateFields.footer)}>
                                            <Input value={templateForm?.content?.footer?.text || ""} placeholder="ex: Mensagem automática. Não responda este número." onChange={(e) => handleChangeTemplateContent("footer", "text", e.target.value)} maxLength={limitsTemplateFields.footer} />
                                        </FormGroup>
                                    }
                                </div>
                                {!templateForm?.content?.call_permission_request &&
                                <div className={styles.buttonsBuilder}>
                                    <div className={styles.buttonsBuilderHeader}>
                                        <div>
                                            <strong>Botões</strong>
                                            <span>Adicione ações opcionais para aparecer abaixo da mensagem.</span>
                                        </div>
                                        <button type="button" onClick={handleAddButton} disabled={(templateForm?.content?.buttons || []).length >= 3}>
                                            <MdAdd /> Adicionar botão
                                        </button>
                                    </div>
                                    {!(templateForm?.content?.buttons || []).length &&
                                        <div className={styles.emptyButtons}>
                                            Nenhum botão adicionado.
                                        </div>
                                    }
                                    {(templateForm?.content?.buttons || []).map((button, index) => (
                                        <div key={index} className={`${styles.buttonBuilder} ${button?.type === "URL" ? (button?.url_mode === "DYNAMIC" ? styles.buttonBuilderUrlDynamic : styles.buttonBuilderUrlStatic) : (["PHONE_NUMBER", "COPY_CODE"].includes(button?.type) ? styles.buttonBuilderWithValue : styles.buttonBuilderSimple)}`}>
                                            <FormGroup labelText={labelWithInfo("Tipo do botão", "Escolha o que acontece quando o cliente toca no botão.")}>
                                                <Select name="Tipo" options={optionsButtonType} selected={button?.type || "QUICK_REPLY"} onChange={(value) => handleChangeButton(index, "type", value)} showOptionAll={false} />
                                            </FormGroup>
                                            <FormGroup labelText={labelWithInfo("Texto do botão", "Use uma ação curta, como Ver pedido ou Falar com atendente.")} rightContent={getCounter(button?.text, limitsTemplateFields.button)}>
                                                <Input value={button?.text || ""} placeholder="ex: Ver pedido" onChange={(e) => handleChangeButton(index, "text", e.target.value)} maxLength={limitsTemplateFields.button} />
                                            </FormGroup>
                                            {button?.type === "URL" &&
                                                <>
                                                    <FormGroup labelText={labelWithInfo("Tipo de URL", "Use link fixo quando todos acessam a mesma página. Use link personalizado quando cada cliente recebe um endereço diferente.")}>
                                                        <Select name="Tipo de URL" options={optionsUrlButtonMode} selected={button?.url_mode || "STATIC"} onChange={(value) => handleChangeButton(index, "url_mode", value)} showOptionAll={false} allowDeselectAll={false} />
                                                    </FormGroup>
                                                    <FormGroup labelText={labelWithInfo(button?.url_mode === "DYNAMIC" ? "URL base" : "URL", button?.url_mode === "DYNAMIC" ? "Parte do link que não muda. O detalhe do cliente entra no final." : "Link aberto quando o cliente tocar no botão.")}>
                                                        <Input value={(button?.url || "").replace(/{{\w+}}/g, "")} placeholder={button?.url_mode === "DYNAMIC" ? "https://loja.com/pedido/" : "https://loja.com/promocao"} onChange={(e) => handleChangeButton(index, "url", e.target.value)} />
                                                    </FormGroup>
                                                    {button?.url_mode === "DYNAMIC" &&
                                                        <FormGroup labelText={labelWithInfo("Exemplo da variável", "Use um exemplo simples para mostrar como o link ficará.")}>
                                                            <Input value={button?.dynamic_example || ""} placeholder="ex: 12345" onChange={(e) => handleChangeButton(index, "dynamic_example", e.target.value)} />
                                                        </FormGroup>
                                                    }
                                                </>
                                            }
                                            {button?.type === "PHONE_NUMBER" &&
                                                <FormGroup labelText={labelWithInfo("Telefone", "Número que será chamado quando o cliente tocar no botão.")}>
                                                    <Input value={button?.phone_number || ""} placeholder="+5511999999999" onChange={(e) => handleChangeButton(index, "phone_number", e.target.value)} />
                                                </FormGroup>
                                            }
                                            {button?.type === "COPY_CODE" &&
                                                <FormGroup labelText={labelWithInfo("Amostra do código", "Use um exemplo do código que pode aparecer para o cliente.")}>
                                                    <Input value={button?.copy_code_example || button?.examples?.[0] || ""} placeholder="ex: PROMO123" onChange={(e) => handleChangeButton(index, "copy_code_example", e.target.value)} />
                                                </FormGroup>
                                            }
                                            <button className={styles.removeButton} type="button" onClick={() => handleRemoveButton(index)}>
                                                <MdOutlineDeleteOutline />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                }
                            </div>
                            <div className={styles.templateFormPreview}>
                                <span>Prévia</span>
                                {templateFormNotice &&
                                    <div className={styles.templateApiNotice}>
                                        <strong>Você está criando uma nova versão</strong>
                                        <span>{templateFormNotice}</span>
                                    </div>
                                }
                                <div className={styles.previewMeta}>
                                    <span>{getTemplateLanguageLabel(templateForm?.language)}</span>
                                    <span>{canSaveTemplateAsDraft() ? (shouldSaveTemplateAsDraft() ? "Rascunho local" : "Será enviado para análise") : getTemplateStatusLabel(templateForm?.status)}</span>
                                </div>
                                <WhatsAppTemplatePreview
                                    template={{ content: getPreviewContent() }}
                                    time="12:45"
                                    className={styles.templateFormWhatsAppPreview}
                                >
                                    {templateForm?.content?.call_permission_request &&
                                        <div className={styles.callPermissionPreview}>
                                            <MdOutlinePhoneInTalk />
                                            <span>Permitir chamadas</span>
                                        </div>
                                    }
                                </WhatsAppTemplatePreview>
                                {getVariablesByText(templateForm?.content?.body?.text).length > 0 &&
                                    <div className={styles.variablesPreview}>
                                        <strong>Variáveis detectadas</strong>
                                        {getVariablesByText(templateForm?.content?.body?.text).map(variable => (
                                            <span key={variable}>{`{{${variable}}}`}</span>
                                        ))}
                                    </div>
                                }
                            </div>
                        </div>
                        <div className={styles.formActions}>
                            <Button variant="primary" type="submit">{getTemplateSubmitButtonText()}</Button>
                            <Button variant="secondary" type="button" onClick={handleCloseModalTemplateForm}>Cancelar</Button>
                        </div>
                    </form>
                </Modal>
            }
            {showModalDeleteTemplate &&
                <ModalConfirm
                    closeModal={handleCloseDeleteTemplate}
                    icon={<MdOutlineDeleteOutline />}
                    title="Exclusão de template"
                    description="Deseja realmente excluir este template?"
                    text={`${templateIdDelete}`.startsWith("local-") ? "Esse rascunho existe apenas na Oktto e será removido da lista." : "A exclusão será enviada para a Meta e removerá o template da Conta do WhatsApp selecionada."}
                    onSubmit={() => handleDeleteTemplate(templateIdDelete)}
                />
            }
            {showModalDeleteSelectedDrafts &&
                <ModalConfirm
                    closeModal={handleCloseDeleteSelectedDrafts}
                    icon={<MdOutlineDeleteOutline />}
                    title="Excluir rascunhos selecionados"
                    description={`${selectedTemplateIds.length} rascunho${selectedTemplateIds.length === 1 ? "" : "s"} selecionado${selectedTemplateIds.length === 1 ? "" : "s"}`}
                    text="Os rascunhos serão removidos somente da Oktto. Esta ação não afeta templates que já existem na Meta e não pode ser desfeita."
                    onSubmit={handleDeleteSelectedDrafts}
                />
            }
            {draftSubmissionConfirmation &&
                <ModalConfirm
                    closeModal={handleCloseDraftSubmissionConfirmation}
                    icon={<MdSend />}
                    title="Enviar para análise"
                    description={`${draftSubmissionConfirmation?.templateIds?.length || 0} template${draftSubmissionConfirmation?.templateIds?.length === 1 ? "" : "s"} selecionado${draftSubmissionConfirmation?.templateIds?.length === 1 ? "" : "s"}`}
                    text="Após a confirmação, os templates serão enviados para análise da Meta e deixarão de existir apenas como rascunhos locais. Deseja continuar?"
                    onSubmit={handleConfirmDraftSubmission}
                />
            }
                </>
            }
        </div>
    )
}

export default TemplatesManager
